import { PencilIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import Cropper from "react-easy-crop";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

import citiesData from "../cities.json";
import statesData from "../states.json";
import Footer from "./Footer";
import InfoTooltip from "./InfoToolTip.js";
import TopBar from "./Topbar";
import useConfig from "../hooks/useConfig";
import useFileValidation from "../hooks/useFileValidation";
import { buildImageUrl } from "../utils/buildImageUrl";
import getCroppedImg from "../utils/cropImageUtils";
import {
    formatInvestorPerkAmount,
    normalizeInvestorPerks,
} from "../utils/normalizeInvestorPerks";
import { notifyUser } from "../utils/notifyUser";

const COMPANY_LOGO_CROP_SIZE = {
    width: 1000,
    height: 1400,
};
const COMPANY_LOGO_CROP_ASPECT =
    COMPANY_LOGO_CROP_SIZE.width / COMPANY_LOGO_CROP_SIZE.height;

// Utility to fetch on hold note (can be moved to a separate file if needed)
async function fetchOnHoldNote(email) {
    try {
        const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/entrepreneur-on-hold-note`,
            {
                params: { email },
                withCredentials: true,
            },
        );
        return res.data;
    } catch{
        return {};
    }
}

function AQIEntrepreneurAccount({ user, setUser }) {
    useEffect(() => {
        console.log("User in AQIEntrepreneurAccount:", user);
    }, [user]);

    const [activeSection, setActiveSection] = useState("generalInfo");
    const [, setCompanyCategory] = useState("");
    const [companyLogo, setCompanyLogo] = useState(null);
    const [formDisabled, setFormDisabled] = useState(false);
    const [, setViewMode] = useState("form");
    const [formErrors, setFormErrors] = useState({});
    const [, setEin] = useState("");
    const [showEin, setShowEin] = useState(false);
    const titleRef = useRef(null);

    const location = useLocation();

    const isDashboardReview =
        location.pathname === "/entrepreneur-dashboard/account-review";

    const [, setCompanyLogoPreview] = useState("");
    const [cities, setCities] = useState([]);
    const fileInputRef = useRef(null);
    const mediaFileInputRef = useRef(null);
    const companyLogoInputRef = useRef(null);
    const navigate = useNavigate();
    const sectionRef = useRef(null);
    const [rejectionInfo, setRejectionInfo] = useState(null);
    const [status, setStatus] = useState("");
    const { getConfig, loading: configLoading } = useConfig();

    const { validateFile, getFileTypeConfig } = useFileValidation();
    const [companyLogoCropImage, setCompanyLogoCropImage] = useState(null);
    const [companyLogoCrop, setCompanyLogoCrop] = useState({ x: 0, y: 0 });
    const [companyLogoZoom, setCompanyLogoZoom] = useState(1);
    const [companyLogoCroppedAreaPixels, setCompanyLogoCroppedAreaPixels] =
        useState(null);
    const [pendingCompanyLogoFileName, setPendingCompanyLogoFileName] =
        useState("");

    const phoneVerificationEnabled =
        getConfig("ENABLE_PHONE_VERIFICATION") === "true";

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [readyToNavigate, setReadyToNavigate] = useState(false);

    // Navigate to dashboard after user state is confirmed updated
    useEffect(() => {
        if(readyToNavigate && user?.application_submitted === 1) {
            navigate("/dashboard");
        }
    }, [readyToNavigate, user, navigate]);

    const [otpSent, setOtpSent] = useState(false);

    const [otp, setOtp] = useState("");

    const [otpVerified, setOtpVerified] = useState(false);

    const [otpLoading, setOtpLoading] = useState(false);

    // On hold feedback state
    const [onHoldNote, setOnHoldNote] = useState("");

    const [onHold, setOnHold] = useState(false);

    const [formData, setFormData] = useState({
        companyName: "",
        companyWebsite: "",
        companyLogo: "",
        dba: "",
        firstName: "",
        lastName: "",
        email: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: "",
        phone: "",
        incorporatedInUS: "",
        executiveTeamUS: "",
        companyCategory: "",
        businessDescription: "",
        missionStatement: "",
        previousFundsRaised: "",
        fundRaisingGoal: "",
        dateFounded: "",
        ein: "",
        maskedEin: "",
        aboutMe: "",
        boardMembers: [{ name: "", title: "" }],
        contactName: "",
        titleRole: "",
        linkedin: "",
        whyInvestment: "",
        equityStake: "",
        achievements: "",
        experience: "",
        projects: "",
        investmentName: "",
        investmentPitch: "",
        investmentMinimum: "",
        investmentState: "",
        investment_logo: "",
        fundingHistory: "",
        revenueGrowth: "",
        equityOffered: "",
        revenueStatus: "",
        tam: "",
        competitiveAdvantage: "",
        valuation: "",
        investorPerks: [{ amount: "", description: "" }],
        risksChallenges: "",
        exitStrategy: "",
        otherInvestors: "",
        videoUrl: "",
        uploadedFiles: {
            businessPlan: "",
            businessPlanPath: "",
            pitchDeck: "",
            pitchDeckPath: "",
            financials: "",
            financialsPath: "",
            taxReturn: "",
            taxReturnPath: "",
            others: [],
        },
        socialLinks: [],
        mediaUploads: [],
    });

    useEffect(() => {
        console.log("Form Data in Review:", formData);
    }, [formData]);

    useEffect(() => {
        if(titleRef.current) {
            titleRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, []);

    // Prefill firstName/lastName from signature_name if present and first/last are empty
    useEffect(() => {
        if(
            formData &&
            formData.signature_name &&
            (!formData.firstName || !formData.lastName)
        ) {
            const parts = formData.signature_name.trim().split(" ");

            if(parts.length > 0) {
                const firstName = parts[0];
                const lastName =
                    parts.length > 1 ? parts.slice(1).join(" ") : "";
                setFormData((prev) => ({
                    ...prev,
                    firstName: prev.firstName || firstName,
                    lastName: prev.lastName || lastName,
                }));
            }
        }
    }, [formData.signature_name]);

    useEffect(() => {
        const path = window.location.pathname;

        if(path.includes("dashboard")) {
            setViewMode("dashboard");
        } else {
            setViewMode("form");
        }
    }, []);

    useEffect(() => {
        if(typeof formData.uploadedFiles === "string") {
            try {
                setFormData((prev) => ({
                    ...prev,
                    uploadedFiles: JSON.parse(prev.uploadedFiles),
                }));
            } catch(err) {
                console.error("Error parsing uploadedFiles:", err);
            }
        }
    }, [formData.uploadedFiles]);

    // useEffect(() => {
    //     if (formDisabled && formData.phone) {
    //         setPhone(formData.phone);
    //     }
    // }, [formDisabled, formData.phone]);
    // ...existing code...

    // Before rendering Board Members input fields (inside the component, before return)
    useEffect(() => {
        if(!formData.boardMembers || formData.boardMembers.length === 0) {
            setFormData((prev) => ({
                ...prev,
                boardMembers: [{ name: "", title: "" }],
            }));
        }
    }, [formData.boardMembers]);

    useEffect(() => {
        const savedData = localStorage.getItem("entrepreneurFormData");

        if(savedData) {
            const parsedSavedData = JSON.parse(savedData);
            setFormData((prev) => ({
                ...prev,
                ...parsedSavedData,
                investorPerks: normalizeInvestorPerks(
                    parsedSavedData.investorPerks,
                ),
            }));
        }

        // const loadedFormData =
        //     JSON.parse(localStorage.getItem("entrepreneurFormData")) || {};

        const fetchPrefillData = async () => {
            const email = user?.email;

            if(!email) return;

            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/entrepreneur-info`,
                    {
                        params: { email },
                        withCredentials: true,
                    },
                );

                const data = response.data;

                if(data.status === "approved") {
                    navigate("/entrepreneur-dashboard");
                    return;
                }

                setFormData((prev) => ({
                    ...prev,
                    firstName: data.firstName || "",
                    lastName: data.lastName || "",
                    email: data.email || "",
                    phone: data.phone || "",
                    address1: data.address1 || "",
                    address2: data.address2 || "",
                    city: data.city || "",
                    state: data.state || "",
                    zipCode: data.zipCode || "",
                    companyName: data.companyName || "",
                    companyLogo: data.companyLogo || "",
                    signature_name: data.signatureName || "",
                    companyWebsite: data.companyWebsite || "",
                    dba: data.dba || "",
                    incorporatedInUS: data.incorporatedInUS || "",
                    executiveTeamUS: data.executiveTeamUS === 1 ? "yes" : "no",
                    companyCategory: data.companyCategory || "",
                    businessDescription: data.businessDescription || "",
                    missionStatement: data.missionStatement || "",
                    previousFundsRaised: data.previousFundsRaised || "",
                    fundRaisingGoal: data.fundRaisingGoal || "",
                    dateFounded: data.dateFounded
                        ? data.dateFounded.split("T")[0]
                        : "",

                    ein: data.ein || data.maskedEin || "",
                    aboutMe: data.aboutMe || "",
                    boardMembers: data.boardMembers
                        ? JSON.parse(data.boardMembers)
                        : [{ name: "", title: "" }],
                    contactName: data.contactName || "",
                    titleRole: data.titleRole || "",
                    linkedin: data.linkedin || "",
                    whyInvestment: data.whyInvestment || "",
                    equityStake: data.equityStake || "",
                    achievements: data.achievements || "",
                    experience: data.experience || "",
                    projects: data.projects || "",
                    investmentName: data.investmentName || "",
                    investmentPitch: data.investmentPitch || "",
                    investmentMinimum: data.investmentMinimum || "",
                    investmentState: data.investmentState || "",
                    investment_logo: data.investment_logo || "",
                    fundingHistory: data.fundingHistory ?? "",
                    revenueGrowth: data.revenueGrowth || "",
                    equityOffered: data.equityOffered || "",
                    revenueStatus: data.revenueStatus || "",
                    tam: data.tam || "",
                    competitiveAdvantage: data.competitiveAdvantage || "",
                    valuation: data.valuation || "",
                    investorPerks: normalizeInvestorPerks(data.investorPerks),
                    risksChallenges: data.risksChallenges || "",
                    exitStrategy: data.exitStrategy || "",
                    otherInvestors: data.otherInvestors || "",
                    videoUrl: data.videoUrl || "",

                    uploadedFiles: (() => {
                        try {
                            let result;

                            if(typeof data.uploadedFiles === "string") {
                                const parsed = JSON.parse(data.uploadedFiles);

                                result = {
                                    businessPlan: parsed?.businessPlan || null,
                                    pitchDeck: parsed?.pitchDeck || null,
                                    financials: parsed?.financials || null,
                                    taxReturn: parsed?.taxReturn || null,
                                    others: Array.isArray(parsed?.others)
                                        ? parsed.others
                                        : [],
                                };
                            } else {
                                result = {
                                    businessPlan:
                                        data.uploadedFiles?.businessPlan ||
                                        null,
                                    pitchDeck:
                                        data.uploadedFiles?.pitchDeck || null,
                                    financials:
                                        data.uploadedFiles?.financials || null,
                                    taxReturn:
                                        data.uploadedFiles?.taxReturn || null,
                                    others: Array.isArray(
                                        data.uploadedFiles?.others,
                                    )
                                        ? data.uploadedFiles.others
                                        : [],
                                };
                            }
                            // Keep this log for debugging - you can remove it once everything works
                            console.log("Processed uploadedFiles:", result);

                            return result;
                        } catch(error) {
                            console.error(
                                "Error processing uploadedFiles:",
                                error,
                            );

                            return {
                                businessPlan: null,
                                pitchDeck: null,
                                financials: null,
                                taxReturn: null,
                                others: [],
                            };
                        }
                    })(),

                    socialLinks: (() => {
                        try {
                            // Check both entrepreneur social links and campaign social links
                            const socialData =
                                data.campaignSocialLinks || data.socialLinks;

                            if(socialData && typeof socialData === "string") {
                                const parsed = JSON.parse(socialData);

                                return Array.isArray(parsed) ? parsed : [];
                            } else if(Array.isArray(socialData)) {
                                return socialData;
                            } else {
                                return [];
                            }
                        } catch(error) {
                            console.warn("Failed to parse socialLinks:", error);

                            return [];
                        }
                    })(),
                    mediaUploads: (() => {
                        try {
                            const mediaData =
                                data.campaignMediaUploads || data.mediaUploads;
                            if(mediaData && typeof mediaData === "string") {
                                const parsed = JSON.parse(mediaData);

                                return Array.isArray(parsed) ? parsed : [];
                            } else if(Array.isArray(mediaData)) {
                                return mediaData;
                            } else {
                                return [];
                            }
                        } catch(error) {
                            console.warn(
                                "Failed to parse mediaUploads:",
                                error,
                            );

                            return [];
                        }
                    })(),

                    status: data.status || "",
                    reEdit: Number(data.re_edit || 0),
                }));

                setStatus(data.status);

                if(data.re_edit === 1) {
                    setFormDisabled(false);
                }

                if(data.status === "rejected") {
                    setRejectionInfo({
                        status: data.status,
                        rejection_note: data.rejection_note || "",
                    });

                    setFormDisabled(true);
                    setIsSubmitted(true);
                } else if(data.status === "pending" && data.submitted) {
                    setIsSubmitted(true);
                    setFormDisabled(true);
                }

                if(response.data.submitted) {
                    setFormDisabled(true);
                }

                if(data.state) {
                    setCities(citiesData[data.state] || []);
                }

                if(data.companyLogo && user?.id) {
                    // Build logo preview URL (handles full S3 URLs and legacy upload paths)
                    const logoPath = data.companyLogo.startsWith("uploads/")
                        ? data.companyLogo
                        : `uploads/${user.id}/logo/${data.companyLogo}`;
                    const logoUrl = buildImageUrl(process.env.REACT_APP_BACKEND_URL, logoPath);
                    setCompanyLogoPreview(logoUrl);
                    setCompanyLogo(data.companyLogo);
                }

                // Handle investment logo preview if it exists
                if(data.investment_logo && user?.id) {
                    // No need to set a preview for investment logo as it's handled directly in the form
                    // But we do need to set the investment_logo field to properly show it's uploaded
                    setFormData((prev) => ({
                        ...prev,
                        investment_logo: data.investment_logo,
                    }));
                }

                // Fetch on hold note
                const holdData = await fetchOnHoldNote(email);

                if(
                    holdData &&
                    holdData.on_hold_note &&
                    holdData.submitted === 0
                ) {
                    setOnHoldNote(holdData.on_hold_note);
                    setOnHold(true);
                } else {
                    setOnHold(false);
                    setOnHoldNote("");
                }
            } catch(error) {
                notifyUser(null, "error", "Failed to prefill data", "toast", null, null, null, null, null, false, false, "top-center");

                console.error(error);
            }
        };

        fetchPrefillData();
    }, [user]);

    useEffect(() => {
        if(formData.state) {
            const availableCities = citiesData[formData.state] || [];

            setCities(availableCities);

            if(!availableCities.includes(formData.city)) {
                setFormData((prev) => ({ ...prev, city: "" }));
            }
        }
    }, [formData.state]);

    useEffect(() => {
        if(formData?.ein) {
            setEin(formData.ein);
        }
    }, [formData]);

    // Fetching Rejection Note
    useEffect(() => {
        const fetchRejectionNote = async () => {
            const email = user?.email;

            if(!email) return;

            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/entrepreneur/rejection-note?email=${email}`,
                    {
                        withCredentials: true,
                    },
                );
                setRejectionInfo(res.data);
                console.log(res.data);
            } catch(err) {
                console.error("Failed to fetch rejection note", err);
            }
        };

        fetchRejectionNote();
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Special handling for phone numbers
        if(name === "phone") {
            // Remove all non-digit characters
            const digitsOnly = value.replace(/\D/g, "");

            // Limit to 10 digits
            const limitedDigits = digitsOnly.substring(0, 10);

            // Format for display only (but store raw digits)
            let formattedPhone = "";

            if(limitedDigits.length > 0) {
                if(limitedDigits.length > 3) {
                    formattedPhone = `(${limitedDigits.substring(
                        0,
                        3,
                    )}) ${limitedDigits.substring(3)}`;

                    if(limitedDigits.length > 6) {
                        formattedPhone = `(${limitedDigits.substring(
                            0,
                            3,
                        )}) ${limitedDigits.substring(
                            3,
                            6,
                        )}-${limitedDigits.substring(6)}`;
                    }
                } else {
                    formattedPhone = `(${limitedDigits}`;
                }
            }

            // Reset verification if phone number changes and verification is enabled
            if(
                formData.phone !== limitedDigits &&
                !configLoading &&
                phoneVerificationEnabled
            ) {
                setOtpVerified(false);
                setOtpSent(false);
                setOtp("");
            }

            // Store the raw digits in the form data, not the formatted display version
            setFormData((prev) => ({ ...prev, [name]: limitedDigits }));

            // Display formatted version in the input (handled via controlled component)
            e.target.value = formattedPhone;

            return;
        }

        // Special handling for currency fields
        if(
            name === "previousFundsRaised" ||
            name === "fundRaisingGoal" ||
            name === "fundingHistory" ||
            name === "revenueGrowth" ||
            name === "valuation"
        ) {
            // Remove all characters except digits (0-9 only)
            const numbersOnly = value.replace(/[^0-9]/g, "");

            setFormData((prev) => ({ ...prev, [name]: numbersOnly }));

            return;
        }

        // Special handling for EIN
        if(name === "ein") {
            const digits = value.replace(/\D/g, "").substring(0, 9);
            let formatted = digits;
            if(digits.length > 2) {
                formatted = `${digits.slice(0, 2)}-${digits.slice(2)}`;
            }
            setFormData((prev) => ({ ...prev, ein: formatted }));
            return;
        }

        // For all other fields, just update the value
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Helper function to format currency for display
    const formatCurrency = (value) => {
        const normalizedValue = String(value ?? "").replace(
            /[^0-9.-]/g,
            "",
        );

        if(!normalizedValue) return "—";

        const number = Number(normalizedValue);
        if(!Number.isFinite(number)) return "—";

        return "$" + number.toLocaleString("en-US");
    };

    const formatEin = (value) => {
        const digits = String(value || "").replace(/\D/g, "").substring(0, 9);

        if(digits.length <= 2) {
            return digits;
        }

        return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    };

    const maskEin = (value) => {
        const digits = String(value || "").replace(/\D/g, "").substring(0, 9);

        if(digits.length <= 4) {
            return digits;
        }

        const maskedPrefix = "x".repeat(Math.max(digits.length - 4, 0));
        const combined = `${maskedPrefix}${digits.slice(-4)}`;

        if(combined.length <= 2) {
            return combined;
        }

        return `${combined.slice(0, 2)}-${combined.slice(2)}`;
    };

    const einDigits = String(formData.ein || "").replace(/\D/g, "").substring(0, 9);
    const einDisplayValue = showEin ? formatEin(einDigits) : maskEin(einDigits);

    const handleEinChange = (value) => {
        setFormData((prev) => ({ ...prev, ein: formatEin(value) }));
    };

    const handleMaskedEinKeyDown = (e) => {
        if(showEin || formDisabled) {
            return;
        }

        const { key, ctrlKey, metaKey } = e;
        const navigationKeys = [
            "Tab",
            "Shift",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "Home",
            "End",
        ];

        if(ctrlKey || metaKey || navigationKeys.includes(key)) {
            return;
        }

        if(key === "Backspace" || key === "Delete") {
            e.preventDefault();
            handleEinChange(einDigits.slice(0, -1));
            return;
        }

        if(/^\d$/.test(key)) {
            e.preventDefault();
            if(einDigits.length < 9) {
                handleEinChange(`${einDigits}${key}`);
            }
            return;
        }

        e.preventDefault();
    };

    const handleMaskedEinPaste = (e) => {
        if(showEin || formDisabled) {
            return;
        }

        e.preventDefault();
        const pastedDigits = e.clipboardData.getData("text").replace(/\D/g, "").substring(0, 9);
        handleEinChange(pastedDigits);
    };

    // Helper function for handling disabled phone verification
    const handleDisabledVerification = () => {
        setOtpVerified(true);
        notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
    };
    // Function to check if phone number is already verified

    const checkPhoneVerification = async (phoneNumber) => {
        // Skip verification if phone number is empty or config is still loading
        if(!phoneNumber || configLoading) return;

        // If phone number is incomplete, don't verify yet
        if(phoneNumber.length < 10) return;

        // If phone verification is disabled, automatically set as verified
        if(!phoneVerificationEnabled) {
            setOtpVerified(true);

            return;
        }

        try {
            // Pass the current user's email to check if phone belongs to another user
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/check-verification/${phoneNumber}`,
                {
                    params: { email: formData.email },
                    withCredentials: true,
                },
            );

            if(res.data.verified) {
                setOtpVerified(true);
                // If verification is disabled, show a message
                if(res.data.disabled) {
                    notifyUser(null, "info", "Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
                }
            } else if(res.data.alreadyInUse) {
                notifyUser(null, "error", "Phone number is already in use by another account. Please use a different number.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(err) {
            if(err.response?.data?.alreadyInUse) {
                notifyUser(null, "error", "Phone number is already in use by another account. Please use a different number.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                console.error(
                    "Failed to check phone verification status:",
                    err,
                );
            }
        }
    };
    const handleSendOtp = async () => {
        // Basic phone validation
        const phoneRegex = new RegExp(
            process.env.REACT_APP_PHONE_REGEX || /^[0-9]{10}$/,
        );

        if(!phoneRegex.test(formData.phone)) {
            notifyUser(null, "error", "Please enter a valid 10-digit phone number.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // If phone verification is disabled, automatically set as verified
        if(!phoneVerificationEnabled) {
            handleDisabledVerification();
            return;
        }

        setOtpLoading(true);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/send-otp`,
                {
                    phoneNumber: formData.phone,
                    email: formData.email,
                },
                {
                    withCredentials: true,
                },
            );

            // Handle disabled verification case
            if(res.data.disabled) {
                setOtpVerified(true);
                notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                setOtpSent(true);
                notifyUser(null, "success", "OTP sent to your phone.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(err) {
            console.error(
                "Send OTP failed:",
                err.response?.data || err.message,
            );

            if(err.response?.data?.alreadyInUse) {
                notifyUser(null, "error", "Phone number is already in use by another account. Please use a different number.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "Failed to send OTP.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } finally {
            setOtpLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if(!otp) {
            notifyUser(null, "error", "Enter the OTP received on your phone.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // If phone verification is disabled, automatically set as verified
        if(!phoneVerificationEnabled) {
            handleDisabledVerification();
            return;
        }

        setOtpLoading(true);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/verify-otp`,
                {
                    phoneNumber: formData.phone,
                    otp,
                    email: formData.email,
                },
                {
                    withCredentials: true,
                },
            );

            // Handle the case when verification is disabled
            if(res.data.disabled) {
                setOtpVerified(true);
                notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                setOtpVerified(true);
                notifyUser(null, "success", "Phone number verified!", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(err) {
            console.error("OTP verification failed:", err);
            notifyUser(null, "error", "Invalid or expired OTP.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setOtpLoading(false);
        }
    };

    // Check phone verification on component mount or when phone number changes
    useEffect(() => {
        if(formData.phone) {
            checkPhoneVerification(formData.phone);
        }
    }, [formData.phone, phoneVerificationEnabled, configLoading]);

    const handleBoardMemberChange = (index, field, value) => {
        const updated = [...formData.boardMembers];

        updated[index][field] = value;

        setFormData((prev) => ({ ...prev, boardMembers: updated }));
    };

    const addBoardMember = () => {
        setFormData((prev) => ({
            ...prev,
            boardMembers: [...prev.boardMembers, { name: "", title: "" }],
        }));
    };

    const removeBoardMember = (index) => {
        const updated = [...formData.boardMembers];

        if(updated.length === 1) {
            notifyUser(null, "error", "At least one board member is required.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        updated.splice(index, 1);

        setFormData((prev) => ({ ...prev, boardMembers: updated }));
    };

    const handleInvestorPerkChange = (index, field, value) => {
        const normalizedValue =
            field === "amount" ? value.replace(/\D/g, "") : value;
        setFormData((prev) => ({
            ...prev,
            investorPerks: normalizeInvestorPerks(prev.investorPerks).map(
                (perk, perkIndex) =>
                    perkIndex === index
                        ? { ...perk, [field]: normalizedValue }
                        : perk,
            ),
        }));
    };

    const addInvestorPerk = () => {
        setFormData((prev) => {
            const perks = normalizeInvestorPerks(prev.investorPerks);
            if(perks.length >= 5) return prev;

            return {
                ...prev,
                investorPerks: [
                    ...perks,
                    { amount: "", description: "" },
                ],
            };
        });
    };

    const removeInvestorPerk = (index) => {
        setFormData((prev) => {
            const perks = normalizeInvestorPerks(prev.investorPerks);
            if(perks.length === 1) {
                return {
                    ...prev,
                    investorPerks: [{ amount: "", description: "" }],
                };
            }

            return {
                ...prev,
                investorPerks: perks.filter(
                    (_, perkIndex) => perkIndex !== index,
                ),
            };
        });
    };

    const getOriginalFileName = (fileName) => {
        // Handle arrays (for required documents stored as arrays)
        if(Array.isArray(fileName)) {
            if(fileName.length > 0 && fileName[0]) {
                return getOriginalFileName(fileName[0]);
            }

            return "No file";
        }

        if(typeof fileName !== "string") {
            console.warn(
                "getOriginalFileName received non-string value:",
                fileName,
            );

            return "Unknown file";
        }

        // Extract filename from path (handles both 'filename.ext' and 'uploads/userId/docType/filename.ext')
        const actualFileName = fileName.split("/").pop();

        // Remove timestamp prefix if it exists (format: timestamp-originalname.ext)
        // Check if it starts with a 13-digit timestamp followed by a dash
        const timestampPattern = /^\d{13}-/;

        if(timestampPattern.test(actualFileName)) {
            return actualFileName.replace(timestampPattern, "");
        }

        // Check for other common timestamp patterns (e.g., 10-digit timestamp)
        const timestampPattern2 = /^\d{10}-/;

        if(timestampPattern2.test(actualFileName)) {
            return actualFileName.replace(timestampPattern2, "");
        }

        return actualFileName;
    };

    // Helper function to check if a file exists for display
    const hasUploadedFile = (fileData) => {
        if(Array.isArray(fileData)) {
            // Check if array has content and first element exists
            return fileData.length > 0 && fileData[0];
        }
        // Check if it's truthy and not empty string
        return !!fileData && fileData !== "";
    };

    // Helper function to get file URL for business documents
    // Uses `buildImageUrl` so it works with S3 full URLs and legacy disk paths
    const getFileUrl = (fileName, docType) => {
        if(!fileName) return null;

        const fileNameStr = typeof fileName === "string" ? fileName : String(fileName);

        // If it's already a full URL, return as-is
        if(fileNameStr.startsWith("http")) return fileNameStr;

        const userId = user?.id;

        if(userId) {
            const path = fileNameStr.startsWith("uploads/")
                ? fileNameStr
                : `uploads/${userId}/${docType}/${fileNameStr}`;

            return buildImageUrl(process.env.REACT_APP_BACKEND_URL, path);
        }

        return buildImageUrl(process.env.REACT_APP_BACKEND_URL, fileNameStr);
    };

    /*
     * File Upload Validation System (Configuration-Based)
     * ============================
     *
     * File types and size limits are now fetched from backend configuration.
     * This allows for dynamic changes without code modifications.
     *
     * Configuration keys:
     * - FILE_EXTENSIONS_DOCUMENTS: Comma-separated list of document extensions
     * - FILE_EXTENSIONS_IMAGES: Comma-separated list of image extensions
     * - FILE_EXTENSIONS_AUDIO: Comma-separated list of audio/video extensions
     * - FILE_SIZE_LIMIT_DOCUMENTS: Maximum size for documents in bytes
     * - FILE_SIZE_LIMIT_IMAGES: Maximum size for images in bytes
     * - FILE_SIZE_LIMIT_AUDIO: Maximum size for audio files in bytes
     *
     * All validations include both MIME type and file extension checks
     * for maximum security and compatibility.
     */

    const uploadLogoFile = async (file, logoType = "companyLogo") => {
        const userId = user?.id;

        if(!userId) {
            notifyUser(null, "error", "User ID is missing. Please log in again.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // Validate image file using configuration
        const validation = validateFile(file, "images");

        if(!validation.isValid) {
            validation.errors.forEach((error) => notifyUser(null, "error", error, "toast", null, null, null, null, null, false, false, "top-center"));
            return;
        }

        // Preview the logo if it's the company logo (not investment logo)
        if(logoType === "companyLogo") {
            const reader = new FileReader();

            reader.onloadend = () => {
                setCompanyLogoPreview(reader.result);
            };

            if(file) {
                reader.readAsDataURL(file);
            }
        }

        try {
            const formData = new FormData();

            formData.append("userId", userId);

            formData.append(
                "docType",
                logoType === "companyLogo" ? "logo" : "investment_logo",
            );

            // Use the appropriate parameter name depending on the logo type
            if(logoType === "companyLogo") {
                formData.append("companyLogo", file);
            } else {
                formData.append("investmentLogo", file);
            }

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/upload?userId=${userId}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                    withCredentials: true,
                },
            );

            // Save file name or path to form state based on logo type
            const uploadedLogo = response.data.files.find(
                (f) =>
                    f.type ===
                    (logoType === "companyLogo" ? "logo" : "investment_logo"),
            );

            if(uploadedLogo) {
                if(logoType === "companyLogo") {
                    setCompanyLogo(file);
                }

                setFormData((prev) => ({
                    ...prev,
                    // Store the uploaded filename in the appropriate field
                    [logoType]: uploadedLogo.path || uploadedLogo.name,
                }));

                notifyUser(null, "success", `${
                        logoType === "companyLogo" ? "Company" : "Investment"
                    } logo uploaded successfully!`, "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", `${
                        logoType === "companyLogo" ? "Company" : "Investment"
                    } logo upload failed.`, "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(error) {
            console.error(`${logoType} upload failed:`, error);

            notifyUser(null, "error", `Failed to upload ${
                    logoType === "companyLogo" ? "company" : "investment"
                } logo.`, "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleCompanyLogoUpload = async (file, logoType = "companyLogo") => {
        if(logoType !== "companyLogo") {
            await uploadLogoFile(file, logoType);
            return;
        }

        const validation = validateFile(file, "images");

        if(!validation.isValid) {
            validation.errors.forEach((error) => notifyUser(null, "error", error, "toast", null, null, null, null, null, false, false, "top-center"));
            return;
        }

        const reader = new FileReader();

        reader.onload = () => {
            setCompanyLogoCropImage(reader.result);
            setPendingCompanyLogoFileName(file.name || "company-logo.jpg");
            setCompanyLogoCrop({ x: 0, y: 0 });
            setCompanyLogoZoom(1);
            setCompanyLogoCroppedAreaPixels(null);
        };

        reader.readAsDataURL(file);
    };

    const handleCompanyLogoCropCancel = () => {
        setCompanyLogoCropImage(null);
        setPendingCompanyLogoFileName("");
        setCompanyLogoCroppedAreaPixels(null);

        if(companyLogoInputRef.current) {
            companyLogoInputRef.current.value = "";
        }
    };

    const handleCompanyLogoCropSave = async () => {
        if(!companyLogoCropImage || !companyLogoCroppedAreaPixels) return;

        try {
            const croppedBlob = await getCroppedImg(
                companyLogoCropImage,
                companyLogoCroppedAreaPixels,
                COMPANY_LOGO_CROP_SIZE,
            );
            const baseName =
                pendingCompanyLogoFileName.replace(/\.[^/.]+$/, "") ||
                "company-logo";
            const croppedFile = new File([croppedBlob], `${baseName}-crop.jpg`, {
                type: "image/jpeg",
            });

            setCompanyLogoCropImage(null);
            setPendingCompanyLogoFileName("");
            await uploadLogoFile(croppedFile, "companyLogo");
        } catch(error) {
            console.error("Company logo crop failed:", error);
            notifyUser(null, "error", "Failed to crop company logo.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            if(companyLogoInputRef.current) {
                companyLogoInputRef.current.value = "";
            }
        }
    };

    const handleSingleFileUpload = async (docType, file) => {

        const userId = user?.id;
        // Only allow .pdf, .xls, .docx
        const allowedExtensions = [".pdf", ".xls", ".docx"];
        const fileName = file?.name?.toLowerCase() || "";
        const isAllowed = allowedExtensions.some(ext => fileName.endsWith(ext));
        if (!isAllowed) {
            notifyUser(null, "error", "Only PDF, XLS, and DOCX files are allowed.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }
        // Validate document file using configuration
        const validation = validateFile(file, "documents");
        if(!validation.isValid) {
            validation.errors.forEach((error) => notifyUser(null, "error", error, "toast", null, null, null, null, null, false, false, "top-center"));
            return;
        }

        const formDataUpload = new FormData();

        formDataUpload.append("docType", docType);
        formDataUpload.append("userId", userId);
        formDataUpload.append("files", file);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/upload`,
                formDataUpload,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );

            const uploaded = res.data.files?.[0]?.name;

            setFormData((prev) => ({
                ...prev,
                uploadedFiles: { ...prev.uploadedFiles, [docType]: uploaded },
            }));

            notifyUser(null, "success", `${docType} uploaded`, "toast", null, null, null, null, null, false, false, "top-center");
        } catch(err) {
            notifyUser(null, "error", `Failed to upload ${docType}`, "toast", null, null, null, null, null, false, false, "top-center");

            console.error(`Error uploading ${docType}:`, err);
        }
    };

    const handleFileDelete = (type) => {
        setFormData((prev) => ({
            ...prev,
            uploadedFiles: { ...prev.uploadedFiles, [type]: null },
        }));
    };

    const handleMultipleFileUpload = async (files) => {

        const userId = user?.id;
        const validFiles = [];
        const allowedExtensions = [".pdf", ".xls", ".docx"];
        for(const file of files) {
            const fileName = file?.name?.toLowerCase() || "";
            const isAllowed = allowedExtensions.some(ext => fileName.endsWith(ext));
            if (!isAllowed) {
                notifyUser(null, "error", `${file.name}: Only PDF, XLS, and DOCX files are allowed.`, "toast", null, null, null, null, null, false, false, "top-center");
                continue;
            }
            const validation = validateFile(file, "documents");
            if(!validation.isValid) {
                validation.errors.forEach((error) =>
                    notifyUser(null, "error", `${file.name}: ${error}`, "toast", null, null, null, null, null, false, false, "top-center"),);
                continue;
            }
            validFiles.push(file);
        }
        if(validFiles.length === 0) {
            notifyUser(null, "error", "No valid files to upload.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const formDataUpload = new FormData();

        formDataUpload.append("userId", userId);

        validFiles.forEach((file) => formDataUpload.append("files", file));
        formDataUpload.append("docType", "others");

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/upload`,
                formDataUpload,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );

            const newFiles = res.data.files.map((f) => {
                // For 'others' files, we need to store both filename and full path
                return {
                    fileName: f.name,
                    fullPath: f.path,
                };
            });

            setFormData((prev) => ({
                ...prev,
                uploadedFiles: {
                    ...prev.uploadedFiles,
                    others: [
                        ...(prev.uploadedFiles?.others || []),
                        ...newFiles,
                    ],
                },
            }));

            notifyUser(null, "success", "Other files uploaded", "toast", null, null, null, null, null, false, false, "top-center");
        } catch{
            notifyUser(null, "error", "Failed to upload additional files", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleOtherDelete = (idx) => {
        setFormData((prev) => {
            const currentOthers = Array.isArray(prev.uploadedFiles?.others)
                ? prev.uploadedFiles.others
                : [];

            const updated = [...currentOthers];

            updated.splice(idx, 1);

            return {
                ...prev,
                uploadedFiles: { ...prev.uploadedFiles, others: updated },
            };
        });
    };

    const handleMediaUpload = async (files) => {
        const validFiles = [];

        // Get configuration-based file limits
        const imageConfig = getFileTypeConfig("images");
        const audioConfig = getFileTypeConfig("audio");
        const documentConfig = getFileTypeConfig("documents");

        // Validate each file - media accepts images, documents, and audio only
        for(const file of files) {
            const errors = [];

            let isValidFileType = false;
            let maxSizeForFile = 0;
            let fileTypeLabel = "";

            if(!file) {
                errors.push("Please select a file.");
                continue;
            }

            // Check if it's an image and apply image validation
            if(
                imageConfig &&
                (imageConfig.extensions.some((ext) =>
                    file.name.toLowerCase().endsWith(ext.toLowerCase()),) ||
                    imageConfig.mimeTypes.some(
                        (mimeType) =>
                            file.type === mimeType ||
                            file.type.startsWith(mimeType.split("/")[0]),
                    ))
            ) {
                maxSizeForFile = imageConfig.maxSize;
                fileTypeLabel = "image";
                isValidFileType = true;
            } else if(
                audioConfig &&
                (audioConfig.extensions.some((ext) =>
                    file.name.toLowerCase().endsWith(ext.toLowerCase()),) ||
                    audioConfig.mimeTypes.some(
                        (mimeType) =>
                            file.type === mimeType ||
                            file.type.startsWith(mimeType.split("/")[0]),
                    ))
            ) {
                maxSizeForFile = audioConfig.maxSize;
                fileTypeLabel = "audio/video";
                isValidFileType = true;
            } else if(
                documentConfig &&
                (documentConfig.extensions.some((ext) =>
                    file.name.toLowerCase().endsWith(ext.toLowerCase()),) ||
                    documentConfig.mimeTypes.some(
                        (mimeType) =>
                            file.type === mimeType ||
                            file.type.startsWith(mimeType.split("/")[0]),
                    ))
            ) {
                maxSizeForFile = documentConfig.maxSize;
                fileTypeLabel = "document";
                isValidFileType = true;
            }

            // If file type is not supported
            if(!isValidFileType) {
                errors.push(
                    `Invalid file type. Only images (${imageConfig?.extensions?.join(
                        ", ",
                    )}), audio files (${audioConfig?.extensions?.join(
                        ", ",
                    )}), and documents (${documentConfig?.extensions?.join(
                        ", ",
                    )}) are supported.`,
                );
            }

            // Check file size if valid file type
            if(isValidFileType && file.size > maxSizeForFile) {
                const maxSizeMB = Math.round(maxSizeForFile / (1024 * 1024));

                errors.push(
                    `File size too large. Maximum size allowed for ${fileTypeLabel} files is ${maxSizeMB}MB`,
                );
            }

            if(errors.length > 0) {
                errors.forEach((error) =>
                    notifyUser(null, "error", `${file.name}: ${error}`, "toast", null, null, null, null, null, false, false, "top-center"),);

                continue;
            }

            validFiles.push(file);
        }

        if(validFiles.length === 0) {
            notifyUser(null, "error", "No valid files to upload.", "toast", null, null, null, null, null, false, false, "top-center");

            return;
        }

        const userId = user?.id;
        const mediaFormData = new FormData();

        mediaFormData.append("userId", userId);
        mediaFormData.append("docType", "media");

        validFiles.forEach((file) => {
            mediaFormData.append("mediaFiles", file);
        });

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/upload`,
                mediaFormData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                },
            );

            const uploadedMedia = response.data.files.map((file) => file.name);

            setFormData((prev) => ({
                ...prev,
                mediaUploads: [
                    ...(Array.isArray(prev.mediaUploads)
                        ? prev.mediaUploads
                        : []),
                    ...uploadedMedia,
                ],
            }));

            notifyUser(null, "success", "Media uploaded successfully!", "toast", null, null, null, null, null, false, false, "top-center");
        } catch(err) {
            console.error("Media upload failed", err);
            notifyUser(null, "error", "Media upload failed", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleMediaDelete = (index) => {
        const currentMediaUploads = Array.isArray(formData.mediaUploads)
            ? formData.mediaUploads
            : [];
        const updated = [...currentMediaUploads];

        updated.splice(index, 1);

        setFormData((prev) => ({ ...prev, mediaUploads: updated }));
    };

    const validateFields = (section, data) => {
        const errors = {};

        if(section === "generalInfo") {
            if(!data.firstName) errors.firstName = "First name is required";

            if(!data.lastName) errors.lastName = "Last name is required";

            if(!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
                errors.email = "Valid email is required";

            if(!data.phone || data.phone.length < 10)
                errors.phone = "Valid phone number required";

            // Only validate phone verification if it's enabled
            if(!configLoading && phoneVerificationEnabled && !otpVerified) {
                errors.phone = "Phone number verification is required";
            }

            if(!data.address1) errors.address1 = "Address is required";

            if(!data.city) errors.city = "City is required";

            if(!data.state) errors.state = "State is required";

            if(!data.zipCode) errors.zipCode = "Zip Code is required";

            if(!data.companyName)
                errors.companyName = "Company name is required";

            if(!data.companyWebsite)
                errors.companyWebsite = "Company website is required";

            if(!data.companyLogo)
                errors.companyLogo = "Company logo is required";
        }

        if(section === "companyDetails") {
            if(!data.incorporatedInUS)
                errors.incorporatedInUS = "Please select an option";

            if(!data.executiveTeamUS)
                errors.executiveTeamUS = "Please select an option";

            if(!data.companyCategory)
                errors.companyCategory = "Company category is required";

            if(!data.dateFounded) {
                errors.dateFounded = "Date founded is required";
            } else {
                const founded = new Date(data.dateFounded);
                const cutoff = new Date();
                cutoff.setMonth(cutoff.getMonth() - 18);
                if(founded > cutoff) {
                    errors.dateFounded = "Company must have been founded at least 18 months ago";
                }
            }

            if(!data.ein?.trim()) {
                errors.ein = "EIN (Tax ID) is required";
            } else {
                const einDigits = data.ein.replace(/\D/g, "");
                if(einDigits.length !== 9) {
                    errors.ein = "EIN must be 9 digits (XX-XXXXXXX)";
                }
            }

            if(
                !data.businessDescription ||
                data.businessDescription.length > 2000
            )
                errors.businessDescription =
                    "Business Description must be up to 2000 characters";

            if(!data.missionStatement || data.missionStatement.length > 500)
                errors.missionStatement =
                    "Mission Statement must be up to 500 characters";

            if(
                !data.fundRaisingGoal ||
                isNaN(data.fundRaisingGoal) ||
                Number(data.fundRaisingGoal) < 10000
            )
                errors.fundRaisingGoal =
                    "Fundraising Goal must be at least $10,000";

            if(!data.boardMembers || data.boardMembers.length < 1) {
                errors.boardMembers = "At least one board member is required";
            } else {
                data.boardMembers.forEach((member, index) => {
                    if(!member.name || !member.title) {
                        errors[`boardMember_${index}`] = `Board member ${
                            index + 1
                        } must have both name and title`;
                    }
                });
            }
        }

        if(section === "uploads") {
            const requiredDocs = [
                "businessPlan",
                "pitchDeck",
                "financials",
                "taxReturn",
            ];

            requiredDocs.forEach((docType) => {
                if(!data.uploadedFiles?.[docType]) {
                    errors[docType] = `${docType.replace(
                        /([A-Z])/g,
                        " $1",
                    )} is required`.replace(/^./, (s) => s.toUpperCase());
                }
            });
        }

        if(section === "accountDetails") {
            if(!data.contactName)
                errors.contactName = "Contact name is required";

            if(!data.aboutMe || data.aboutMe.length > 500)
                errors.aboutMe =
                    "About Me is required and can be up to 500 characters long";

            if(!data.titleRole) errors.titleRole = "Title/Role is required";

            if(!/^https?:\/\//.test(data.linkedin))
                errors.linkedin = "LinkedIn must be a valid URL";

            if(!data.whyInvestment || data.whyInvestment.length > 2000)
                errors.whyInvestment = "Why Investment is required";

            const stake = Number(data.equityStake);

            if(isNaN(stake) || stake < 0 || stake > 100)
                errors.equityStake = "Equity Stake must be between 0 and 100";

            if(!data.achievements)
                errors.achievements = "Key achievements are required";

            if(!data.experience) errors.experience = "Experience is required";

            if(!data.projects) errors.projects = "Projects is required";
        }

        if(section === "investmentDetails") {
            if(!data.investmentName)
                errors.investmentName = "Investment name is required";

            if(!data.investmentMinimum)
                errors.investmentMinimum = "Investment minimum is required";

            if(!data.investmentState)
                errors.investmentState = "Investment state is required";

            if(!data.investment_logo)
                errors.investment_logo = "Investment logo is required";

            if(!data.revenueGrowth)
                errors.revenueGrowth = "Revenue growth is required";

            if(!data.equityOffered)
                errors.equityOffered = "Equity offered is required";

            if(!data.investmentPitch || data.investmentPitch.length > 2000)
                errors.investmentPitch =
                    "Investment pitch must be up to 2000 characters";

            if(!data.fundingHistory || data.fundingHistory.length > 2000)
                errors.fundingHistory =
                    "Funding History must be up to 2000 characters";

            if(!data.equityOffered || data.equityOffered.length > 2000)
                errors.equityOffered =
                    "Equity Offered must be up to 2000 characters";

            if(!data.revenueStatus || data.revenueStatus.length > 2000)
                errors.revenueStatus =
                    "Revenue Status must be up to 2000 characters";
        }

        if(section === "investmentDetailsCont") {
            if(!data.tam) errors.tam = "TAM is required";

            if(!data.risksChallenges)
                errors.risksChallenges = "Risks and Challenges are required";

            if(!data.competitiveAdvantage)
                errors.competitiveAdvantage =
                    "Competitive Advantage is required";

            if(!data.exitStrategy)
                errors.exitStrategy = "Exit Strategy is required";

            if(!data.otherInvestors)
                errors.otherInvestors = "Other investors info is required";

            const investorPerks = normalizeInvestorPerks(data.investorPerks);
            const completedPerks = investorPerks.filter(
                (perk) => perk.amount.trim() && perk.description.trim(),
            );

            if(completedPerks.length === 0) {
                errors.investorPerks =
                    "At least one investor perk with an amount and description is required";
            } else if(
                investorPerks.some(
                    (perk) =>
                        (perk.amount.trim() || perk.description.trim()) &&
                        (!perk.amount.trim() || !perk.description.trim()),
                )
            ) {
                errors.investorPerks =
                    "Each investor perk must include both an amount and description";
            }
        }

        if(section === "socialLinks") {
            const socialLinksArray = Array.isArray(data.socialLinks)
                ? data.socialLinks
                : [];

            if(
                socialLinksArray.length === 0 ||
                socialLinksArray.every((link) => !link)
            )
                errors.socialLinks =
                    "Please provide at least one social media link";
        }

        if(section === "mediaAndPress") {
            const url = data.videoUrl?.trim();

            if(!url) {
                errors.videoUrl = "Video URL is required.";
            } else {
                const isYouTube =
                    url.includes("youtube.com") || url.includes("youtu.be");
                const isVimeo = url.includes("vimeo.com");

                if(!isYouTube && !isVimeo) {
                    errors.videoUrl =
                        "Video URL must be from YouTube or Vimeo.";
                }
            }
        }

        return errors;
    };

    const handleContinue = async () => {
        // Always initialize email at the very top
        const errors = validateFields(activeSection, formData);

        console.log("Validation Errors:", errors);

        if(Object.keys(errors).length > 0) {
            setFormErrors(errors);
            notifyUser(null, "error", "Please correct the highlighted fields.", "toast", null, null, null, null, null, false, false, "top-center");

            return;
        } else {
            setFormErrors({});
        }

        // Prepare data to save for current section
        const sectionData = {};

        const fieldsBySection = {
            generalInfo: [
                "firstName",
                "lastName",
                "email",
                "phone",
                "address1",
                "address2",
                "city",
                "state",
                "zipCode",
                "companyName",
                "companyWebsite",
                "companyLogo",
                "dba",
            ],
            companyDetails: [
                "incorporatedInUS",
                "executiveTeamUS",
                "companyCategory",
                "dateFounded",
                "businessDescription",
                "missionStatement",
                "fundRaisingGoal",
                "previousFundsRaised",
                "boardMembers",
                "ein",
            ],
            uploads: ["uploadedFiles"],
            accountDetails: [
                "contactName",
                "aboutMe",
                "titleRole",
                "linkedin",
                "whyInvestment",
                "equityStake",
                "achievements",
                "experience",
                "projects",
            ],
            investmentDetails: [
                "investmentName",
                "investmentPitch",
                "investmentMinimum",
                "investmentState",
                "investment_logo",
                "fundingHistory",
                "revenueGrowth",
                "equityOffered",
                "revenueStatus",
            ],
            investmentDetailsCont: [
                "tam",
                "risksChallenges",
                "competitiveAdvantage",
                "exitStrategy",
                "valuation",
                "otherInvestors",
                "investorPerks",
            ],
            socialLinks: ["socialLinks"],
            mediaAndPress: ["mediaUploads", "videoUrl"],
        };

        const keys = fieldsBySection[activeSection] || [];

        keys.forEach((key) => {
            sectionData[key] = formData[key];
        });

        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/save-progress`,
                {
                    section: activeSection,
                    data: sectionData,
                },
                {
                    withCredentials: true,
                },
            );

            notifyUser(null, "success", "Progress saved", "toast", null, null, null, null, null, false, false, "top-center");
        } catch(err) {
            console.error("Failed to save progress", err);
            //  toast.error("Failed to save progress");
        }

        // Move to next section
        const order = [
            "generalInfo",
            "companyDetails",
            "uploads",
            "accountDetails",
            "investmentDetails",
            "investmentDetailsCont",
            "socialLinks",
            "mediaAndPress",
            "review",
        ];

        const nextIndex = order.indexOf(activeSection) + 1;

        if(nextIndex < order.length) setActiveSection(order[nextIndex]);

        localStorage.setItem("entrepreneurFormData", JSON.stringify(formData));
    };

    // const handleFileDelete = (index) => {
    //     const updatedFiles = [...formData.uploadedFiles];
    //     updatedFiles.splice(index, 1);
    //     setFormData(prev => ({ ...prev, uploadedFiles: updatedFiles }));
    // };

    const handleBack = () => {
        if(activeSection === "companyDetails") {
            setActiveSection("generalInfo");
        } else if(activeSection === "uploads") {
            setActiveSection("companyDetails");
        } else if(activeSection === "accountDetails") {
            setActiveSection("uploads");
        } else if(activeSection === "investmentDetails") {
            setActiveSection("accountDetails");
        } else if(activeSection === "investmentDetailsCont") {
            setActiveSection("investmentDetails");
        } else if(activeSection === "socialLinks") {
            setActiveSection("investmentDetailsCont");
        } else if(activeSection === "mediaAndPress") {
            setActiveSection("socialLinks");
        } else if(activeSection === "review") {
            setActiveSection("mediaAndPress");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const allSections = [
            "generalInfo",
            "companyDetails",
            "uploads",
            "accountDetails",
            "investmentDetails",
            "investmentDetailsCont",
            "socialLinks",
            "mediaAndPress",
        ];

        let combinedErrors = {};

        allSections.forEach((section) => {
            const errors = validateFields(section, formData);
            combinedErrors = { ...combinedErrors, ...errors };
        });

        if(Object.keys(combinedErrors).length > 0) {
            setFormErrors(combinedErrors);
            notifyUser(null, "error", "Please correct the errors before submitting.", "toast", null, null, null, null, null, false, false, "top-center");

            return;
        }

        try {
            // Using the correct endpoint /api/auth/account-creation-details
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/account-creation-details`,
                formData,
                {
                    withCredentials: true,
                },
            );

            console.log("Submitting data:", formData);

                if(response.status === 200) {
                notifyUser(null, "success", <div>
                        <h3 className="text-lg font-semibold">You're All Set!</h3>
                        <p className="text-sm mt-1">Thank you for completing your entrepreneur application! You will now be redirecting to your entrepreneur dashboard</p>
                    </div>, "toast", null, null, 8000, null, null, false, false, "top-center");

                localStorage.removeItem("entrepreneurFormData");
                localStorage.removeItem("editMode");

                setTimeout(() => {
                    const updatedUser = {
                        ...(user || {}),
                        role: "entrepreneur",
                        submitted: true,
                        application_submitted: 1,
                        isProfileVerified: "true",
                    };
                    localStorage.setItem("user", JSON.stringify(updatedUser));

                    if(setUser) {
                        setUser((prev) => ({
                            ...prev,
                            role: "entrepreneur",
                            submitted: true,
                            application_submitted: 1,
                            isProfileVerified: "true",
                        }));
                    }

                    const userUpdatedEvent = new CustomEvent("user-logged-in", {
                        detail: updatedUser,
                    });
                    window.dispatchEvent(userUpdatedEvent);

                    setReadyToNavigate(true);
                }, 3000);
            } else {
                notifyUser(null, "error", "Submission failed", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(err) {
            console.error(
                "Submission error:",
                err.response?.data || err.message || err,
            );

            notifyUser(null, "error", "An error occurred during submission", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const progress =
        activeSection === "generalInfo"
            ? "12.5%"
            : activeSection === "companyDetails"
              ? "25%"
              : activeSection === "uploads"
                ? "37.5%"
                : activeSection === "accountDetails"
                  ? "50%"
                  : activeSection === "investmentDetails"
                    ? "62.5%"
                    : activeSection === "investmentDetailsCont"
                      ? "75%"
                      : activeSection === "socialLinks"
                        ? "87.5%"
                        : activeSection === "mediaAndPress"
                          ? "100%"
                          : "100%";

    useEffect(() => {
        document.title = "entrepreneurAccountCreation | AQI";
    }, []);

    // Before rendering Board Members input fields (inside the component, before return)
    useEffect(() => {
        if(!formData.boardMembers || formData.boardMembers.length === 0) {
            setFormData((prev) => ({
                ...prev,
                boardMembers: [{ name: "", title: "" }],
            }));
        }
    }, [formData.boardMembers]);

    return (
        <div className="font-poppins">
            <>
                {!isDashboardReview && <TopBar />}
                {formDisabled && (
                    <>
                        {formDisabled &&
                            isSubmitted &&
                            status === "pending" &&
                            formData.reEdit == 1 && (
                                <div className="bg-green-100 text-green-800 p-3 sm:p-4 rounded-xl mb-4 text-xs sm:text-sm font-semibold text-center font-poppins mx-2 sm:mx-4">
                                    Your edit request has been approved.{" "}
                                    <span
                                        className="underline text-navy cursor-pointer"
                                        onClick={() =>
                                            navigate("/entrepreneur-dashboard")
                                        }
                                    >
                                        Click here to update your entrepreneur
                                        account.
                                    </span>
                                </div>
                            )}
                        {rejectionInfo?.status !== "rejected" &&
                            formData.reEdit !== 1 &&
                            isSubmitted && (
                                <div className="bg-yellow-100 text-[#840C28] p-2 sm:p-3 mb-4 text-xs sm:text-sm font-regular italic text-center font-poppins mx-2 sm:mx-4">
                                    Your application has been submitted and is
                                    under review. You can view it, but edits are
                                    disabled.
                                </div>
                            )}

                        {rejectionInfo?.status === "rejected" &&
                            rejectionInfo?.rejection_note && (
                                <div className="bg-red-100 text-[#840C28] p-3 sm:p-4 mb-4 text-xs sm:text-sm font-medium italic text-center font-poppins whitespace-pre-line mx-2 sm:mx-4">
                                    <strong>Rejection Note:</strong>{" "}
                                    {rejectionInfo.rejection_note}
                                </div>
                            )}
                    </>
                )}

                {onHold && onHoldNote && (
                    <div className="bg-yellow-100 text-[#840C28] p-3 rounded-xl mb-4 text-sm font-medium italic text-center font-poppins mx-2">
                        <strong>Account On Hold:</strong> {onHoldNote}
                    </div>
                )}
                <div
                    ref={titleRef}
                    className="min-h-screen flex flex-col bg-white"
                >
                    <main className="flex-grow flex justify-center px-2 sm:px-4 lg:px-6">
                        <div
                            ref={sectionRef}
                            className="w-full max-w-sm sm:max-w-2xl md:max-w-4xl lg:max-w-6xl xl:max-w-7xl bg-white rounded-xl shadow-xl pt-3 sm:pt-4 lg:pt-6 px-3 sm:px-6 md:px-8 lg:px-10 pb-4 sm:pb-6 lg:pb-8"
                        >
                            <h1 className="text-center text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold font-poppins text-navy">
                                {activeSection === "generalInfo" &&
                                    "Create Your AQi Account"}
                                {activeSection === "companyDetails" &&
                                    "Company Details"}
                                {activeSection === "uploads" &&
                                    "Attach Business Documents"}
                                {activeSection === "accountDetails" &&
                                    "Account Details"}
                                {activeSection === "investmentDetails" &&
                                    "Investment Opportunity Details"}
                                {activeSection === "investmentDetailsCont" &&
                                    "Investment Opportunity Details (cont.)"}
                                {activeSection === "socialLinks" &&
                                    "Link Company Social Media Accounts"}
                                {activeSection === "mediaAndPress" &&
                                    "Attach Media and Press"}
                            </h1>
                            {activeSection !== "review" && (
                                <div className="w-full bg-gray-200 h-1.5 sm:h-2 lg:h-3 mt-3 sm:mt-4 lg:mt-6">
                                    <div
                                        className="bg-cyan-700 h-1.5 sm:h-2 lg:h-3 transition-all duration-500 rounded-xl"
                                        style={{ width: progress }}
                                    ></div>
                                </div>
                            )}
                            {activeSection === "generalInfo" && (
                                <div>
                                    <form className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mt-4 sm:mt-6">
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Company Legal Name*:
                                            </label>
                                            <input
                                                name="companyName"
                                                value={formData.companyName}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.companyName && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.companyName}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Company Logo*:
                                            </label>
                                            <input
                                                ref={companyLogoInputRef}
                                                type="file"
                                                accept=".gif,.png,.jpeg,.jpg,image/gif,image/png,image/jpeg"
                                                onChange={(e) => {
                                                    const file =
                                                        e.target.files?.[0];

                                                    if(file) {
                                                        handleCompanyLogoUpload(
                                                            file,
                                                        );
                                                    }
                                                }}
                                                className="p-1 sm:p-1 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                            <p className="mt-1 text-xs text-gray-600 italic">
                                                Only GIF, PNG, JPEG, and JPG
                                                images are supported (Max: 15MB)
                                            </p>
                                            {companyLogo && (
                                                <span className="text-xs sm:text-sm text-gray-700 mt-1 font-poppins">
                                                    Selected file:{" "}
                                                    <span className="font-medium break-words">
                                                        {typeof companyLogo ===
                                                        "string"
                                                            ? companyLogo
                                                                  .split("/")
                                                                  .pop()
                                                            : companyLogo.name ||
                                                              ""}
                                                    </span>
                                                </span>
                                            )}
                                            {formErrors.companyLogo && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.companyLogo}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Company Website:*
                                            </label>
                                            <input
                                                name="companyWebsite"
                                                value={formData.companyWebsite}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.companyWebsite && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.companyWebsite}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                DBA:
                                            </label>
                                            <input
                                                name="dba"
                                                value={formData.dba}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.dba && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.dba}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Email Address:*
                                            </label>
                                            <input
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={true}
                                            />
                                            {formErrors.email && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.email}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Primary Contact First Name:*
                                            </label>
                                            <input
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.firstName && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.firstName}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Primary Contact Last Name:*
                                            </label>
                                            <input
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.lastName && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.lastName}
                                                </p>
                                            )}
                                        </div>{" "}
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Phone Number: *
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="(###) ###-####"
                                                onChange={handleChange}
                                                name="phone"
                                                value={
                                                    formData.phone
                                                        ? formData.phone.replace(
                                                              /(\d{3})(\d{3})(\d{4})/,
                                                              "($1) $2-$3",
                                                          )
                                                        : ""
                                                }
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.phone && (
                                                <p className="text-red-600 text-xs sm:text-sm">
                                                    {formErrors.phone}
                                                </p>
                                            )}

                                            {!configLoading && (
                                                <>
                                                    {phoneVerificationEnabled ? (
                                                        <>
                                                            {otpVerified && (
                                                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-1 gap-2">
                                                                    <p className="text-green-600 text-xs sm:text-sm font-semibold">
                                                                        Phone
                                                                        number
                                                                        verified
                                                                    </p>
                                                                    {!formDisabled && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setOtpVerified(
                                                                                    false,
                                                                                );
                                                                                setOtpSent(
                                                                                    false,
                                                                                );
                                                                                setOtp(
                                                                                    "",
                                                                                );
                                                                            }}
                                                                            className="text-xs sm:text-sm text-[#840C28] underline font-medium self-start sm:self-auto"
                                                                        >
                                                                            Change
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {!formDisabled &&
                                                                !otpVerified && (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            onClick={
                                                                                handleSendOtp
                                                                            }
                                                                            className="mt-2 w-full sm:w-fit bg-navy text-white py-1 sm:py-1 px-4 rounded-xl text-xs sm:text-sm font-semibold hover:bg-navy-2"
                                                                            disabled={
                                                                                otpLoading
                                                                            }
                                                                        >
                                                                            {otpLoading
                                                                                ? "Verifying Phone Number..."
                                                                                : "Verify Phone Number"}
                                                                        </button>

                                                                        {otpSent && (
                                                                            <div className="mt-2 flex flex-col gap-2">
                                                                                <input
                                                                                    type="text"
                                                                                    placeholder="Enter OTP"
                                                                                    value={
                                                                                        otp
                                                                                    }
                                                                                    onChange={(
                                                                                        e,
                                                                                    ) =>
                                                                                        setOtp(
                                                                                            e
                                                                                                .target
                                                                                                .value,
                                                                                        )
                                                                                    }
                                                                                    className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                                                />
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={
                                                                                        handleVerifyOtp
                                                                                    }
                                                                                    className="w-full sm:w-fit bg-green-600 text-white py-1 sm:py-1 px-4 rounded-xl text-xs sm:text-sm font-semibold hover:bg-green-700"
                                                                                    disabled={
                                                                                        otpLoading
                                                                                    }
                                                                                >
                                                                                    {otpLoading
                                                                                        ? "Verifying..."
                                                                                        : "Verify Code"}
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                        </>
                                                    ) : null}
                                                </>
                                            )}

                                            {configLoading && (
                                                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                                    Loading verification
                                                    settings...
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Company Street Address:*
                                            </label>
                                            <input
                                                name="address1"
                                                value={formData.address1}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.address1 && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.address1}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                State:*
                                            </label>
                                            <select
                                                name="state"
                                                value={formData.state}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            >
                                                <option value="">
                                                    Select State
                                                </option>
                                                {Object.keys(statesData).map(
                                                    (s) => (
                                                        <option
                                                            key={s}
                                                            value={s}
                                                        >
                                                            {statesData[s]}
                                                        </option>
                                                    ),
                                                )}
                                            </select>
                                            {formErrors.state && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.state}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                City:*
                                            </label>
                                            <select
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            >
                                                <option value="">
                                                    Select City
                                                </option>
                                                {cities.map((c, i) => (
                                                    <option key={i}>{c}</option>
                                                ))}
                                            </select>
                                            {formErrors.city && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.city}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Zip Code:*
                                            </label>
                                            <input
                                                name="zipCode"
                                                value={formData.zipCode}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.zipCode && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.zipCode}
                                                </p>
                                            )}
                                        </div>
                                        <div className="col-span-1 lg:col-span-2 flex justify-center sm:justify-end mt-2 sm:mt-4">
                                            <button
                                                type="button"
                                                onClick={handleContinue}
                                                className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-navy text-white rounded-xl shadow-md hover:bg-navy-2 font-bold font-poppins text-sm sm:text-base"
                                            >
                                                {formDisabled
                                                    ? "CONTINUE"
                                                    : "SAVE & CONTINUE"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                            {activeSection === "companyDetails" && (
                                <>
                                    <form
                                        onSubmit={handleSubmit}
                                        className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mt-4 sm:mt-6"
                                    >
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                How is your company legally
                                                registered?*
                                            </label>
                                            <select
                                                name="incorporatedInUS"
                                                value={
                                                    formData.incorporatedInUS
                                                }
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            >
                                                <option value="">Select</option>
                                                <option value="C-Corp">
                                                    C-Corp
                                                </option>
                                                <option value="S-Corp">
                                                    S-Corp
                                                </option>
                                                <option value="Sole Proprietorship">
                                                    Sole Proprietorship
                                                </option>
                                                <option value="General Partnership">
                                                    General Partnership
                                                </option>
                                                <option value="Limited Liability Partnership (LLP)">
                                                    Limited Liability
                                                    Partnership (LLP)
                                                </option>
                                                <option value="Limited Liability Corporation (LLC)">
                                                    Limited Liability
                                                    Corporation (LLC)
                                                </option>
                                                <option value="Limited Partnership (LP)">
                                                    Limited Partnership (LP)
                                                </option>
                                            </select>
                                            {formErrors.incorporatedInUS && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {
                                                        formErrors.incorporatedInUS
                                                    }
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Total Capital raised
                                                <span className="text-xs sm:text-sm">
                                                    (before this campaign):*
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                name="previousFundsRaised"
                                                placeholder="$##,###.00"
                                                value={
                                                    formData.previousFundsRaised
                                                }
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.previousFundsRaised && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {
                                                        formErrors.previousFundsRaised
                                                    }
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Is your executive team located
                                                in the United States?*
                                            </label>
                                            <select
                                                name="executiveTeamUS"
                                                value={formData.executiveTeamUS}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            >
                                                <option value="">Select</option>
                                                <option value="yes">Yes</option>
                                                <option value="no">No</option>
                                            </select>
                                            {formErrors.executiveTeamUS && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.executiveTeamUS}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                How much are you looking to
                                                raise?*
                                            </label>
                                            <input
                                                type="text"
                                                name="fundRaisingGoal"
                                                placeholder="$##,###.00"
                                                value={formData.fundRaisingGoal}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.fundRaisingGoal && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.fundRaisingGoal}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base mt-2">
                                                Company Category:*
                                            </label>
                                            <select
                                                value={formData.companyCategory}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setCompanyCategory(val);
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        companyCategory: val,
                                                    }));
                                                }}
                                                className="w-full p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base mt-1"
                                                disabled={formDisabled}
                                                required
                                            >
                                                <option value="">
                                                    Select Company Category
                                                </option>
                                                <option value="Aerospace and Space Technology">Aerospace and Space Technology</option>
                                                <option value="Agriculture and AgTech">Agriculture and AgTech</option>
                                                <option value="Beauty & Personal Care">Beauty & Personal Care</option>
                                                <option value="Biotechnology">Biotechnology</option>
                                                <option value="Carbon Capture & Climate Technology">Carbon Capture & Climate Technology</option>
                                                <option value="Conservation & Biodiversity">Conservation & Biodiversity</option>
                                                <option value="Consumer Applications">Consumer Applications</option>
                                                <option value="Consumer Packaged Goods (CPG)">Consumer Packaged Goods (CPG)</option>
                                                <option value="Education and EdTech">Education and EdTech</option>
                                                <option value="Financial Services and Fintech">Financial Services and Fintech</option>
                                                <option value="Food Service and Hospitality">Food Service and Hospitality</option>
                                                <option value="Gaming and Esports">Gaming and Esports</option>
                                                <option value="Health Care Equipment & Supplies">Health Care Equipment & Supplies</option>
                                                <option value="Health Care Providers & Services">Health Care Providers & Services</option>
                                                <option value="Health Care Technology">Health Care Technology</option>
                                                <option value="Insurance and InsurTech">Insurance and InsurTech</option>
                                                <option value="Life Sciences Tools & Services">Life Sciences Tools & Services</option>
                                                <option value="Manufacturing and Industrial/Hardware">Manufacturing and Industrial/Hardware</option>
                                                <option value="Materials, Chemicals and Sustainable Packaging">Materials, Chemicals and Sustainable Packaging</option>
                                                <option value="Media and Entertainment">Media and Entertainment</option>
                                                <option value="Pharmaceuticals">Pharmaceuticals</option>
                                                <option value="Professional and Business Services">Professional and Business Services</option>
                                                <option value="Real Estate">Real Estate</option>
                                                <option value="Renewable & Clean Energy">Renewable & Clean Energy</option>
                                                <option value="Retail">Retail</option>
                                                <option value="Sports, Fitness & Wellness">Sports, Fitness & Wellness</option>
                                                <option value="Technology">Technology</option>
                                                <option value="Transportation and Mobility">Transportation and Mobility</option>
                                                <option value="Waste Management & Circular Economy">Waste Management & Circular Economy</option>
                                                <option value="Water Technology & Conservation">Water Technology & Conservation</option>
                                                <option value="Other">Other</option>
                                            </select>
                                            {formErrors.companyCategory && (
                                                <p className="text-red-600 text-xs sm:text-sm">
                                                    {formErrors.companyCategory}
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Date Founded:
                                            </label>
                                            <input
                                                type="date"
                                                name="dateFounded"
                                                value={formData.dateFounded}
                                                onChange={handleChange}
                                                max={(() => {
                                                    const d = new Date();
                                                    d.setMonth(d.getMonth() - 18);
                                                    return d.toISOString().split("T")[0];
                                                })()}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                disabled={formDisabled}
                                            />
                                        </div>
                                        <div className="col-span-1 lg:col-span-2 flex flex-col lg:flex-row gap-4">
                                            <div className="w-full lg:w-1/2 flex flex-col">
                                                <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                    EIN#*
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        name="ein"
                                                        type="text"
                                                        placeholder="12-3456789"
                                                        value={einDisplayValue}
                                                        onChange={(e) => {
                                                            if(showEin && !formDisabled) {
                                                                handleEinChange(e.target.value);
                                                            }
                                                        }}
                                                        onKeyDown={handleMaskedEinKeyDown}
                                                        onPaste={handleMaskedEinPaste}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                        maxLength={10}
                                                        className="w-full p-2 sm:p-2 pr-10 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                        disabled={formDisabled}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowEin((prev) => !prev)}
                                                        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-navy"
                                                        aria-label={showEin ? "Hide EIN" : "Show EIN"}
                                                    >
                                                        {showEin ? <FaEyeSlash /> : <FaEye />}
                                                    </button>
                                                </div>

                                                {formErrors.ein && (
                                                    <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                        {formErrors.ein}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="w-full lg:w-1/2 flex flex-col">
                                                <label className="text-navy font-semibold font-poppins text-sm sm:text-base mb-2">
                                                    Board Members (Name and
                                                    Title):
                                                </label>

                                                {(
                                                    formData.boardMembers || []
                                                ).map((member, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex flex-col sm:flex-row gap-2 items-start sm:items-center mb-3"
                                                    >
                                                        <input
                                                            type="text"
                                                            placeholder="Enter member's full name"
                                                            value={member.name}
                                                            onChange={(e) =>
                                                                handleBoardMemberChange(
                                                                    index,
                                                                    "name",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full sm:w-1/2"
                                                            disabled={
                                                                formDisabled
                                                            }
                                                        />
                                                        <input
                                                            type="text"
                                                            placeholder="Enter title/role"
                                                            value={member.title}
                                                            onChange={(e) =>
                                                                handleBoardMemberChange(
                                                                    index,
                                                                    "title",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full sm:w-1/2"
                                                            disabled={
                                                                formDisabled
                                                            }
                                                        />
                                                        {!formDisabled && (
                                                            <div className="flex gap-2 sm:gap-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={
                                                                        addBoardMember
                                                                    }
                                                                    className="text-navy font-bold text-lg sm:text-xl p-1"
                                                                    title="Add"
                                                                >
                                                                    +
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        removeBoardMember(
                                                                            index,
                                                                        )
                                                                    }
                                                                    className="text-red-600 font-bold text-lg sm:text-xl p-1"
                                                                    title="Remove"
                                                                >
                                                                    −
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base mt-4">
                                                Short Business Description:
                                            </label>
                                            <textarea
                                                name="businessDescription"
                                                value={
                                                    formData.businessDescription
                                                }
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                rows="3"
                                                maxLength={128}
                                                disabled={formDisabled}
                                            />
                                            {formErrors.businessDescription && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {
                                                        formErrors.businessDescription
                                                    }
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex flex-col">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Give us your quick pitch
                                                <p className="text-xs sm:text-sm font-regular">
                                                    What does your business do,
                                                    and why does it matter? *
                                                </p>
                                            </label>
                                            <textarea
                                                name="missionStatement"
                                                value={
                                                    formData.missionStatement
                                                }
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                rows="3"
                                                maxLength={128}
                                                disabled={formDisabled}
                                            />
                                            {formErrors.missionStatement && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {
                                                        formErrors.missionStatement
                                                    }
                                                </p>
                                            )}
                                        </div>

                                        <div className="col-span-1 lg:col-span-2 flex flex-col sm:flex-row justify-between gap-4 mt-6">
                                            <button
                                                type="button"
                                                onClick={handleBack}
                                                className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-[#ffffff] border-2 border-navy text-navy rounded-xl shadow-md hover:bg-[#ffffff] font-bold font-poppins text-sm sm:text-base"
                                            >
                                                BACK
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleContinue}
                                                className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-navy text-white rounded-xl shadow-md hover:bg-navy-2 font-bold font-poppins text-sm sm:text-base"
                                            >
                                                {formDisabled
                                                    ? "CONTINUE"
                                                    : "SAVE & CONTINUE"}
                                            </button>
                                        </div>
                                    </form>
                                </>
                            )}
                            {activeSection === "uploads" && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
                                    {[
                                        "businessPlan",
                                        "pitchDeck",
                                        "financials",
                                        "taxReturn",
                                    ].map((type) => (
                                        <div
                                            key={type}
                                            className="flex flex-col"
                                        >
                                            <label className="text-navy font-semibold capitalize text-sm sm:text-base">
                                                {type.replace(
                                                    /([A-Z])/g,
                                                    " $1",
                                                )}
                                                *
                                            </label>

                                            {!formDisabled ? (
                                                <>
                                                    <input
                                                        type="file"
                                                        accept=".doc,.docx,.xlsx,.pdf,.pptx,.xls,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                                        onChange={(e) =>
                                                            handleSingleFileUpload(
                                                                type,
                                                                e.target
                                                                    .files[0],
                                                            )
                                                        }
                                                        className="p-1 sm:p-1 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                                    />
                                                    <p className="mt-1 text-xs text-gray-600 italic">
                                                        Only DOCX,
                                                        PDF and XLS
                                                        documents are supported
                                                        (Max: 25MB)
                                                    </p>
                                                </>
                                            ) : hasUploadedFile(
                                                  formData.uploadedFiles?.[
                                                      type
                                                  ],
                                              ) ? (
                                                <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded border mt-1">
                                                    <div className="text-lg">
                                                        📄
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-xs text-gray-600 font-medium">
                                                            Uploaded Document:
                                                        </p>
                                                        <a
                                                            href={getFileUrl(
                                                                formData
                                                                    .uploadedFiles[
                                                                    type
                                                                ],
                                                                type,
                                                            )}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 hover:text-blue-800 text-sm underline break-words"
                                                        >
                                                            {getOriginalFileName(
                                                                formData
                                                                    .uploadedFiles[
                                                                    type
                                                                ],
                                                            )}
                                                        </a>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-xs sm:text-sm mt-1 text-red-500">
                                                    No file uploaded
                                                </p>
                                            )}

                                            {!formDisabled &&
                                                hasUploadedFile(
                                                    formData.uploadedFiles?.[
                                                        type
                                                    ],
                                                ) && (
                                                    <div className="mt-2 flex items-center justify-between p-2 bg-gray-50 rounded border">
                                                        <div className="flex items-center space-x-2 flex-1">
                                                            <div className="flex-1">
                                                                <a
                                                                    href={getFileUrl(
                                                                        formData
                                                                            .uploadedFiles[
                                                                            type
                                                                        ],
                                                                        type,
                                                                    )}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-blue-600 hover:text-blue-800 text-sm underline break-words"
                                                                >
                                                                    {getOriginalFileName(
                                                                        formData
                                                                            .uploadedFiles[
                                                                            type
                                                                        ],
                                                                    )}
                                                                </a>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() =>
                                                                handleFileDelete(
                                                                    type,
                                                                )
                                                            }
                                                            className="text-red-600 hover:text-red-800 text-lg ml-2 flex-shrink-0"
                                                        >
                                                            ❌
                                                        </button>
                                                    </div>
                                                )}
                                            {formErrors[type] && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors[type]}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                    <div className="w-full col-span-1 lg:col-span-2 mt-4 sm:mt-6">
                                        <label className="text-navy font-semibold text-sm sm:text-base mb-2 block">
                                            Other Supporting Documents
                                        </label>
                                        {!formDisabled && (
                                            <>
                                                <div
                                                    className="w-full h-48 sm:h-56 lg:h-64 border-2 border-dashed border-navy flex flex-col justify-center items-center cursor-pointer rounded-xl p-4"
                                                    onClick={() =>
                                                        fileInputRef.current.click()
                                                    }
                                                    onDragOver={(e) =>
                                                        e.preventDefault()
                                                    }
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        const files =
                                                            Array.from(
                                                                e.dataTransfer
                                                                    .files,
                                                            );
                                                        handleMultipleFileUpload(
                                                            files,
                                                        );
                                                    }}
                                                >
                                                    <p className="mb-2 font-semibold text-center text-sm sm:text-base">
                                                        DRAG & DROP FILES HERE
                                                    </p>
                                                    <p className="text-xs sm:text-sm text-center">
                                                        or click to browse from
                                                        your computer
                                                    </p>
                                                    <input
                                                        type="file"
                                                        accept=".doc,.docx,.xlsx,.pdf,.pptx,.xls,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                                        multiple
                                                        ref={fileInputRef}
                                                        className="hidden"
                                                        onChange={(e) =>
                                                            handleMultipleFileUpload(
                                                                Array.from(
                                                                    e.target
                                                                        .files,
                                                                ),
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <p className="mt-2 text-xs text-gray-600 italic">
                                                    Only DOCX, PDF,
                                                    and XLS documents are
                                                    supported (Max: 25MB each)
                                                </p>
                                            </>
                                        )}

                                        {(formData.uploadedFiles?.others || [])
                                            .length > 0 && (
                                            <div className="mt-4">
                                                <h3 className="text-navy font-semibold text-sm sm:text-base mb-2">
                                                    Uploaded:
                                                </h3>
                                                <ul className="text-xs sm:text-sm text-gray-700 space-y-2">
                                                    {(() => {
                                                        return formData
                                                            .uploadedFiles
                                                            .others;
                                                    })()
                                                        .map(
                                                            (
                                                                fileInfo,
                                                                originalIndex,
                                                            ) => {
                                                                console.log(
                                                                    "Processing fileInfo:",
                                                                    fileInfo,
                                                                    "at index:",
                                                                    originalIndex,
                                                                );
                                                                // Handle both object and string formats
                                                                if(
                                                                    fileInfo &&
                                                                    typeof fileInfo ===
                                                                        "object" &&
                                                                    fileInfo.fileName
                                                                ) {
                                                                    return {
                                                                        fileInfo,
                                                                        originalIndex,
                                                                    };
                                                                } else if(
                                                                    typeof fileInfo ===
                                                                    "string"
                                                                ) {
                                                                    // Convert string to object format
                                                                    const converted =
                                                                        {
                                                                            fileName:
                                                                                fileInfo
                                                                                    .split(
                                                                                        "/",
                                                                                    )
                                                                                    .pop(),
                                                                            fullPath:
                                                                                fileInfo,
                                                                        };
                                                                    return {
                                                                        fileInfo:
                                                                            converted,
                                                                        originalIndex,
                                                                    };
                                                                } else if(
                                                                    fileInfo &&
                                                                    typeof fileInfo ===
                                                                        "object"
                                                                ) {
                                                                    // Object without fileName - try to extract from other properties
                                                                    // const fileName =
                                                                    //     fileInfo.fullPath
                                                                    //         ? fileInfo.fullPath
                                                                    //               .split(
                                                                    //                   "/"
                                                                    //               )
                                                                    //               .pop()
                                                                    //         : fileInfo.name ||
                                                                    //           "Unknown file";
                                                                    return {
                                                                        fileInfo:
                                                                            originalIndex,
                                                                    };
                                                                }
                                                                return null;
                                                            },
                                                        )
                                                        .filter(
                                                            (item) =>
                                                                item !== null,
                                                        )
                                                        .map(
                                                            ({
                                                                fileInfo,
                                                                originalIndex,
                                                            }) => {
                                                                console.log(
                                                                    "Rendering fileInfo:",
                                                                    fileInfo,
                                                                    "fileName:",
                                                                    fileInfo.fileName,
                                                                );
                                                                return (
                                                                    <li
                                                                        key={
                                                                            originalIndex
                                                                        }
                                                                        className="flex justify-between items-center"
                                                                    >
                                                                        <a
                                                                            href={`${process.env.REACT_APP_BACKEND_URL}/${fileInfo.fullPath}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-blue-600 underline"
                                                                        >
                                                                            {getOriginalFileName(
                                                                                fileInfo.fileName,
                                                                            )}
                                                                        </a>
                                                                        {!formDisabled && (
                                                                            <button
                                                                                onClick={() =>
                                                                                    handleOtherDelete(
                                                                                        originalIndex,
                                                                                    )
                                                                                }
                                                                                className="text-red-600 hover:text-red-800 ml-2"
                                                                            >
                                                                                ❌
                                                                            </button>
                                                                        )}
                                                                    </li>
                                                                );
                                                            },
                                                        )}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-span-1 lg:col-span-2 flex flex-col sm:flex-row justify-between gap-4 mt-6">
                                        <button
                                            type="button"
                                            onClick={handleBack}
                                            className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-[#ffffff] border-2 border-navy text-navy rounded-xl shadow-md hover:bg-[#ffffff] font-bold font-poppins text-sm sm:text-base"
                                        >
                                            BACK
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleContinue}
                                            className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-navy text-white rounded-xl shadow-md hover:bg-navy-2 font-bold font-poppins text-sm sm:text-base"
                                        >
                                            {formDisabled
                                                ? "CONTINUE"
                                                : "SAVE & CONTINUE"}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {activeSection === "accountDetails" && (
                                <form className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mt-4 sm:mt-6">
                                    {/* LEFT COLUMN */}
                                    <div className="flex flex-col gap-3 sm:gap-4">
                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Primary Company Contact Full
                                                Name: *
                                                <InfoTooltip
                                                    id="primaryContact"
                                                    content=" The full (first, middle, and last) legal name of the person responsible for this campaign—usually a founder or executive who can answer questions and provide updates. "
                                                />
                                            </label>
                                            <input
                                                name="contactName"
                                                value={formData.contactName}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />

                                            {formErrors.contactName && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.contactName}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                About Me: *
                                                <InfoTooltip
                                                    id="aboutMe"
                                                    content=" A short personal bio from the founder or team member—sharing who you are, what drives you, and why you're building this business. "
                                                />
                                            </label>
                                            <textarea
                                                name="aboutMe"
                                                value={formData.aboutMe}
                                                onChange={handleChange}
                                                rows="4"
                                                className="p-1 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.aboutMe && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.aboutMe}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Title/Role in the Investment
                                                Opportunity: *
                                                <InfoTooltip
                                                    id="titleRole"
                                                    content="Your position and responsibilities in the company or campaign—like Founder, CEO, or Marketing Lead—so investors know who’s behind the vision. "
                                                />
                                            </label>
                                            <input
                                                name="titleRole"
                                                value={formData.titleRole}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.titleRole && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.titleRole}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                LinkedIn Profile: *
                                                <InfoTooltip
                                                    id="linkedin"
                                                    content="A link to your professional LinkedIn page so investors can learn more about your background, experience, and connections. "
                                                />
                                            </label>
                                            <input
                                                name="linkedin"
                                                value={formData.linkedin}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.linkedin && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.linkedin}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* RIGHT COLUMN */}
                                    <div className="flex flex-col gap-3 sm:gap-4">
                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Why this investment matters to
                                                you: *
                                                <InfoTooltip
                                                    id="whyInvestment"
                                                    content="A personal note from the founder about why this business matters—sharing the mission, the motivation, and why now is the time to invest. "
                                                />
                                            </label>
                                            <input
                                                name="whyInvestment"
                                                value={formData.whyInvestment}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.whyInvestment && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.whyInvestment}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Equity Stake (%): *
                                                <InfoTooltip
                                                    id="equityStake"
                                                    content=" The portion of ownership a company is offering to investors in exchange for funding. It shows how much equity the founder is willing to part with to raise capital."
                                                />
                                            </label>
                                            <input
                                                name="equityStake"
                                                value={formData.equityStake}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.equityStake && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.equityStake}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Key Achievements: *
                                                <InfoTooltip
                                                    id="keyAchievements"
                                                    content=" The big wins your company has accomplished so far—like awards, milestones, partnerships, or growth. This is your moment to brag investors want to see traction! "
                                                />
                                            </label>
                                            <input
                                                name="achievements"
                                                value={formData.achievements}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.achievements && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.achievements}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                How long have you been in the
                                                industry: *
                                                <InfoTooltip
                                                    id="howLongInIndustry"
                                                    content=" Share how many years of experience you (or your team) have in this field. Investors want to know you're not new to the game. "
                                                />
                                            </label>
                                            <select
                                                name="experience"
                                                value={formData.experience}
                                                onChange={handleChange}
                                                className="p-1.5 sm:p-1.5 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            >
                                                <option value="">
                                                    Select Range
                                                </option>
                                                <option value="0-3">
                                                    0 to 3 Years
                                                </option>
                                                <option value="3-6">
                                                    3 to 6 Years
                                                </option>
                                                <option value="6-9">
                                                    6 to 9 Years
                                                </option>
                                                <option value="9-12">
                                                    9 to 12 Years
                                                </option>
                                                <option value="12-15">
                                                    12 to 15 Years
                                                </option>
                                                <option value="> 15">
                                                    above 15 Years
                                                </option>
                                            </select>

                                            {formErrors.experience && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.experience}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Past Venture or Notable
                                                Projects:*
                                                <InfoTooltip
                                                    id="pastProjects"
                                                    content="Tell us about any businesses, startups, or standout projects you’ve led or been part of before. It shows your track record—and gives investors confidence. "
                                                />
                                            </label>
                                            <input
                                                name="projects"
                                                value={formData.projects}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.projects && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.projects}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Submit Button - full width */}
                                    <div className="col-span-1 lg:col-span-2 flex flex-col sm:flex-row justify-between gap-4 mt-6">
                                        <button
                                            type="button"
                                            onClick={handleBack}
                                            className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-[#ffffff] border-2 border-navy text-navy rounded-xl shadow-md hover:bg-[#ffffff] font-bold font-poppins text-sm sm:text-base"
                                        >
                                            BACK
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleContinue}
                                            className="w-full sm:w-auto py-3 sm:py-2 px-8 sm:px-12 bg-navy text-white rounded-xl shadow-md hover:bg-navy-2 font-bold font-poppins text-sm sm:text-base"
                                        >
                                            {formDisabled
                                                ? "CONTINUE"
                                                : "SAVE & CONTINUE"}
                                        </button>
                                    </div>
                                </form>
                            )}
                            {activeSection === "investmentDetails" && (
                                <form className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mt-4 sm:mt-6">
                                    {/* LEFT COLUMN */}
                                    <div className="flex flex-col gap-3 sm:gap-4">
                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Investment Name: *
                                                <InfoTooltip
                                                    id="investmentName"
                                                    content="A short name for your fundraising campaign—usually your company name or a project title that helps investors easily recognize what they’re supporting. "
                                                />
                                            </label>
                                            <input
                                                name="investmentName"
                                                value={formData.investmentName}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.investmentName && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.investmentName}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Short Investment Pitch: *
                                                <InfoTooltip
                                                    id="shortInvestmentPitch"
                                                    content=" Your business in a nutshell—what you do, why it matters, and why now’s the time to invest. Make it clear, compelling, and under 280 characters (yes, like a tweet!). "
                                                />
                                            </label>
                                            <textarea
                                                name="investmentPitch"
                                                value={formData.investmentPitch}
                                                onChange={handleChange}
                                                rows="5"
                                                className="p-1 sm:p-1 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.investmentPitch && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.investmentPitch}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Investment Minimum: *
                                                <InfoTooltip
                                                    id="investmentMinimum"
                                                    content="The smallest amount an investor can contribute to your campaign—it sets the entry point for backing your business. Common minimums are $100, $250, or $1,000—you choose what fits your funding strategy. "
                                                />
                                            </label>
                                            <input
                                                name="investmentMinimum"
                                                type="number"
                                                min="0"
                                                value={
                                                    formData.investmentMinimum ===
                                                        undefined ||
                                                    formData.investmentMinimum ===
                                                        null
                                                        ? ""
                                                        : formData.investmentMinimum
                                                }
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    handleChange({
                                                        target: {
                                                            name: "investmentMinimum",
                                                            value:
                                                                val === ""
                                                                    ? ""
                                                                    : Number(
                                                                          val,
                                                                      ),
                                                        },
                                                    });
                                                }}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.investmentMinimum && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {
                                                        formErrors.investmentMinimum
                                                    }
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Current Incorporation State: *
                                                <InfoTooltip
                                                    id="currentIncorporationState"
                                                    content=" The U.S. state where your company is legally registered—like Delaware, California, or Nebraska. This is where your business officially “lives.”"
                                                />
                                            </label>
                                            <select
                                                name="investmentState"
                                                value={formData.investmentState}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            >
                                                <option value="">
                                                    Select State
                                                </option>
                                                {Object.keys(statesData).map(
                                                    (stateCode) => (
                                                        <option
                                                            key={stateCode}
                                                            value={stateCode}
                                                        >
                                                            {
                                                                statesData[
                                                                    stateCode
                                                                ]
                                                            }
                                                        </option>
                                                    ),
                                                )}
                                            </select>
                                            {formErrors.investmentState && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.investmentState}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* RIGHT COLUMN */}
                                    <div className="flex flex-col gap-3 sm:gap-4">
                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Upload Investment Logo *
                                            </label>
                                            <input
                                                type="file"
                                                accept=".gif,.png,.jpeg,.jpg,image/gif,image/png,image/jpeg"
                                                onChange={(e) =>
                                                    handleCompanyLogoUpload(
                                                        e.target.files[0],
                                                        "investment_logo",
                                                    )
                                                }
                                                className="p-[0.4rem] sm:p-[0.4rem] border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            <p className="mt-1 text-xs text-gray-600 italic">
                                                Only GIF, PNG, JPEG, and JPG
                                                images are supported (Max: 15MB)
                                            </p>
                                            {formData.investment_logo && (
                                                <p className="mt-1 break-all text-xs text-green-700">
                                                    Uploaded: {getOriginalFileName(
                                                        formData.investment_logo,
                                                    )}
                                                </p>
                                            )}

                                            {formErrors.investment_logo && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.investment_logo}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                How much have you raised so far?
                                                *
                                                <InfoTooltip
                                                    id="fundingHistory"
                                                    content="The total amount of money your company has raised so far in this campaign—whether from friends and family, angel investors, or other sources. It shows your momentum! "
                                                />
                                            </label>
                                            <input
                                                name="fundingHistory"
                                                value={formData.fundingHistory}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.fundingHistory && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.fundingHistory}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Projected Revenue Growth: *
                                                <InfoTooltip
                                                    id="revenueGrowth"
                                                    content="Your best estimate of how much your company’s revenue will grow over time—based on market research, trends, and your business plan. It shows investors where you’re headed. "
                                                />
                                            </label>
                                            <input
                                                name="revenueGrowth"
                                                value={formData.revenueGrowth}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.revenueGrowth && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.revenueGrowth}
                                                </p>
                                            )}
                                        </div>

                                        <div className="mt-[0.625rem]">
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Equity Offered: *
                                                <InfoTooltip
                                                    id="equity-info"
                                                    content={`The percentage of company ownership being made available to investors during this fundraising campaign.
EXAMPLE:
If a company is raising $500,000 and offers 10% equity, that means investors will collectively own 10% of the company after the raise.

Founders choose how much equity to offer based on how much funding they need and the company’s current valuation.

Revenue Status – A quick snapshot of whether your company is currently making money. Are you pre-revenue, earning revenue, or already profitable?`}
                                                />
                                            </label>
                                            <input
                                                name="equityOffered"
                                                value={formData.equityOffered}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.equityOffered && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.equityOffered}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                                Revenue Status: *
                                                <InfoTooltip
                                                    id="revenueStatus"
                                                    content="A quick snapshot of whether your company is currently making money. Are you pre-revenue, earning revenue, or already profitable?"
                                                />
                                            </label>
                                            <input
                                                name="revenueStatus"
                                                value={formData.revenueStatus}
                                                onChange={handleChange}
                                                className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                disabled={formDisabled}
                                            />
                                            {formErrors.revenueStatus && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                    {formErrors.revenueStatus}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Submit or Continue */}
                                    <div className="col-span-1 lg:col-span-2 flex flex-col sm:flex-row justify-between gap-4 mt-6">
                                        <button
                                            type="button"
                                            onClick={handleBack}
                                            className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-[#ffffff] border-2 border-navy text-navy rounded-xl shadow-md hover:bg-[#ffffff] font-bold font-poppins text-sm sm:text-base"
                                        >
                                            BACK
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleContinue}
                                            className="w-full sm:w-auto py-3 sm:py-2 px-8 sm:px-12 bg-navy text-white rounded-xl shadow-md hover:bg-navy-2 font-bold font-poppins text-sm sm:text-base"
                                        >
                                            {formDisabled
                                                ? "CONTINUE"
                                                : "SAVE & CONTINUE"}
                                        </button>
                                    </div>
                                </form>
                            )}
                            {activeSection === "investmentDetailsCont" && (
                                <form className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mt-4 sm:mt-6">
                                    <div className="flex flex-col">
                                        <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                            TAM: *
                                            <InfoTooltip
                                                id="tam-info"
                                                content="Total Addressable Market (TAM) – The total revenue opportunity available for a product or service."
                                            />
                                        </label>
                                        <input
                                            name="tam"
                                            value={formData.tam}
                                            onChange={handleChange}
                                            className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                            disabled={formDisabled}
                                        />
                                        {formErrors.tam && (
                                            <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                {formErrors.tam}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                            Key Risks and Challenges: *
                                            <InfoTooltip
                                                id="keyRisks"
                                                content=" Every business has hurdles. This is where you list at least 8 honest risks your company could face—like market changes, competition, supply chain issues, or team gaps. Being transparent builds trust with investors. "
                                            />
                                        </label>
                                        <input
                                            name="risksChallenges"
                                            value={formData.risksChallenges}
                                            onChange={handleChange}
                                            className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                            disabled={formDisabled}
                                        />
                                        {formErrors.risksChallenges && (
                                            <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                {formErrors.risksChallenges}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                            Competitive Advantage: *
                                            <InfoTooltip
                                                id="competitiveAdvantage"
                                                content="What makes your company stand out from the crowd—whether it’s your product, team, technology, or approach. This is why you're built to win. "
                                            />
                                        </label>
                                        <input
                                            name="competitiveAdvantage"
                                            value={
                                                formData.competitiveAdvantage
                                            }
                                            onChange={handleChange}
                                            className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base"
                                            disabled={formDisabled}
                                        />
                                        {formErrors.competitiveAdvantage && (
                                            <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                {
                                                    formErrors.competitiveAdvantage
                                                }
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                            Exit Strategy/Liquidity Event: *
                                            <InfoTooltip
                                                id="exit-info"
                                                content="A planned method for investors to realize their returns, such as selling the company or going public"
                                            />
                                        </label>
                                        <input
                                            name="exitStrategy"
                                            value={formData.exitStrategy}
                                            onChange={handleChange}
                                            className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                            disabled={formDisabled}
                                        />
                                        {formErrors.exitStrategy && (
                                            <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                {formErrors.exitStrategy}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                            Valuation: *
                                            <InfoTooltip
                                                id="valuation-info"
                                                content="The estimated current value of your company, which helps determine how much equity you're offering to investors"
                                            />
                                        </label>
                                        <input
                                            name="valuation"
                                            value={formData.valuation}
                                            onChange={handleChange}
                                            className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                            disabled={formDisabled}
                                        />
                                        {formErrors.valuation && (
                                            <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                {formErrors.valuation}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                            Other Notable Investors: *
                                            <InfoTooltip
                                                id="otherInvestors"
                                                content="If you’ve got well-known names, firms, or mentors backing you—now’s the time to name-drop (nicely). It shows investors you’re already catching smart attention. "
                                            />
                                        </label>
                                        <input
                                            name="otherInvestors"
                                            value={formData.otherInvestors}
                                            onChange={handleChange}
                                            className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                            disabled={formDisabled}
                                        />
                                        {formErrors.otherInvestors && (
                                            <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                {formErrors.otherInvestors}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-col">
                                        <label className="text-navy font-semibold font-poppins text-sm sm:text-base">
                                            Investor Perks: *
                                            <InfoTooltip
                                                id="investorPerks"
                                                content=" The extra goodies investors get for backing your campaign—like swag, shoutouts, early access, or exclusive experiences. Think of it as a thank-you with style! "
                                            />
                                        </label>
                                        <span className="mb-2 text-xs text-gray-500">
                                            Add up to 5 perks
                                        </span>
                                        {normalizeInvestorPerks(
                                            formData.investorPerks,
                                        ).map((perk, index) => (
                                            <div
                                                key={index}
                                                className="mb-3 flex flex-col items-start gap-2 sm:flex-row sm:items-center"
                                            >
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    placeholder="Investment amount"
                                                    maxLength={30}
                                                    value={perk.amount}
                                                    onChange={(e) =>
                                                        handleInvestorPerkChange(
                                                            index,
                                                            "amount",
                                                            e.target.value,
                                                        )
                                                    }
                                                    className="h-11 w-full rounded-xl border border-navy p-2 font-poppins text-sm sm:w-1/3 sm:text-base"
                                                    disabled={formDisabled}
                                                />
                                                <textarea
                                                    placeholder="Describe the investor perk"
                                                    value={perk.description}
                                                    onChange={(e) =>
                                                        handleInvestorPerkChange(
                                                            index,
                                                            "description",
                                                            e.target.value,
                                                        )
                                                    }
                                                    maxLength={500}
                                                    rows={1}
                                                    className="h-11 w-full resize-none rounded-xl border border-navy p-2 font-poppins text-sm sm:w-2/3 sm:text-base"
                                                    disabled={formDisabled}
                                                />
                                                {!formDisabled && (
                                                    <div className="flex gap-2 sm:gap-1">
                                                        {normalizeInvestorPerks(
                                                            formData.investorPerks,
                                                        ).length < 5 && (
                                                            <button
                                                                type="button"
                                                                onClick={
                                                                    addInvestorPerk
                                                                }
                                                                className="p-1 text-lg font-bold text-navy sm:text-xl"
                                                                title="Add perk"
                                                            >
                                                                +
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                removeInvestorPerk(
                                                                    index,
                                                                )
                                                            }
                                                            className="p-1 text-lg font-bold text-red-600 sm:text-xl"
                                                            title="Remove perk"
                                                        >
                                                            &minus;
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {formErrors.investorPerks && (
                                            <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                {formErrors.investorPerks}
                                            </p>
                                        )}
                                    </div>
                                    <div className="col-span-1 lg:col-span-2 flex flex-col sm:flex-row justify-between gap-4 mt-6">
                                        <button
                                            type="button"
                                            onClick={handleBack}
                                            className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-[#ffffff] border-2 border-navy text-navy rounded-xl shadow-md hover:bg-[#ffffff] font-bold font-poppins text-sm sm:text-base"
                                        >
                                            BACK
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleContinue}
                                            className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-navy text-white rounded-xl shadow-md hover:bg-navy-2 font-bold font-poppins text-sm sm:text-base"
                                        >
                                            {formDisabled
                                                ? "CONTINUE"
                                                : "SAVE & CONTINUE"}
                                        </button>
                                    </div>
                                </form>
                            )}
                            {activeSection === "socialLinks" && (
                                <form className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mt-4 sm:mt-6 w-full">
                                    {/* Social Links Grid */}
                                    <div className="col-span-1 lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 w-full">
                                        {[1, 2, 3, 4].map((num) => (
                                            <div key={num} className="w-full">
                                                <label className="text-navy font-semibold font-poppins text-sm sm:text-base mb-2 block">
                                                    Social #{num}:*
                                                </label>
                                                <input
                                                    name={`social${num}`}
                                                    value={
                                                        (Array.isArray(
                                                            formData.socialLinks,
                                                        )
                                                            ? formData
                                                                  .socialLinks[
                                                                  num - 1
                                                              ]
                                                            : "") || ""
                                                    }
                                                    onChange={(e) => {
                                                        const currentSocialLinks =
                                                            Array.isArray(
                                                                formData.socialLinks,
                                                            )
                                                                ? formData.socialLinks
                                                                : [];
                                                        const updated = [
                                                            ...currentSocialLinks,
                                                        ];
                                                        updated[num - 1] =
                                                            e.target.value;
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            socialLinks:
                                                                updated,
                                                        }));
                                                    }}
                                                    placeholder="adding link will discover which social media account"
                                                    className="p-2 sm:p-2 border border-navy rounded-xl font-poppins text-sm sm:text-base w-full"
                                                    disabled={formDisabled}
                                                />
                                                {formErrors.socialLinks && (
                                                    <p className="text-red-600 text-xs sm:text-sm mt-1">
                                                        {formErrors.socialLinks}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Footer Button */}
                                    <div className="col-span-1 lg:col-span-2 flex flex-col sm:flex-row justify-between gap-4 mt-6">
                                        <button
                                            type="button"
                                            onClick={handleBack}
                                            className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-[#ffffff] border-2 border-navy text-navy rounded-xl shadow-md hover:bg-[#ffffff] font-bold font-poppins text-sm sm:text-base"
                                        >
                                            BACK
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleContinue}
                                            className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-navy text-white rounded-xl shadow-md hover:bg-navy-2 font-bold font-poppins text-sm sm:text-base"
                                        >
                                            {formDisabled
                                                ? "CONTINUE"
                                                : "SAVE & CONTINUE"}
                                        </button>
                                    </div>
                                </form>
                            )}
                            {activeSection === "mediaAndPress" && (
                                <div className="mt-6">
                                    {/* Drag and Drop Area */}
                                    <div
                                        className="w-full h-48 border-2 border-dashed border-gray-400 flex flex-col justify-center items-center text-center text-gray-700 text-sm mb-6 cursor-pointer rounded-xl p-4 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                                        onClick={() =>
                                            !formDisabled &&
                                            mediaFileInputRef.current.click()
                                        }
                                        onDragEnter={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onDragLeave={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if(!formDisabled) {
                                                const files = Array.from(
                                                    e.dataTransfer.files,
                                                );
                                                handleMediaUpload(files);
                                            }
                                        }}
                                    >
                                        <p className="font-semibold mb-2">
                                            DRAG & DROP FILES HERE
                                        </p>
                                        <p className="text-xs">
                                            Upload images, audio/video, or
                                            documents
                                        </p>
                                        <input
                                            type="file"
                                            ref={mediaFileInputRef}
                                            multiple
                                            accept=".gif,.png,.jpeg,.jpg,.mp4,.wav,.mp3,.doc,.docx,.xlsx,.pdf,.pptx,.xls,image/gif,image/png,image/jpeg,audio/mpeg,audio/wav,video/mp4,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                            className="hidden"
                                            onChange={(e) =>
                                                handleMediaUpload(
                                                    Array.from(e.target.files),
                                                )
                                            }
                                            disabled={formDisabled}
                                        />
                                    </div>
                                    <p className="mb-6 text-xs text-gray-600 italic">
                                        Images: GIF, PNG, JPEG, JPG (Max: 15MB)
                                        | Audio: MP4, WAV, MP3 (Max: 25MB) |
                                        Documents: DOC, DOCX, XLSX, PDF, PPTX,
                                        XLS (Max: 25MB)
                                    </p>

                                    {/* Media File List */}

                                    {(Array.isArray(formData.mediaUploads)
                                        ? formData.mediaUploads
                                        : []
                                    ).length > 0 ? (
                                        <ul className="space-y-2 mb-6">
                                            {(Array.isArray(
                                                formData.mediaUploads,
                                            )
                                                ? formData.mediaUploads
                                                : []
                                            ).map((file, idx) => {
                                                const fileName =
                                                    getOriginalFileName(file);
                                                const fileUrl = file.startsWith(
                                                    "http",
                                                )
                                                    ? file
                                                    : `${process.env.REACT_APP_BACKEND_URL}/${file}`;
                                                const isImage =
                                                    /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(
                                                        fileName,
                                                    );
                                                const isAudio =
                                                    /\.(mp3|wav|ogg|m4a|aac)$/i.test(
                                                        fileName,
                                                    );
                                                const isVideo =
                                                    /\.(mp4|avi|mov|wmv|flv|webm)$/i.test(
                                                        fileName,
                                                    );
                                                const isDocument =
                                                    /\.(doc|docx|xlsx|pdf|pptx|xls)$/i.test(
                                                        fileName,
                                                    );

                                                return (
                                                    <li
                                                        key={idx}
                                                        className="flex flex-col p-3 border border-gray-300 rounded-xl"
                                                    >
                                                        <div className="flex justify-between items-center mb-2">
                                                            <a
                                                                href={fileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-sm text-blue-600 hover:text-blue-800 font-medium underline cursor-pointer"
                                                            >
                                                                {fileName}
                                                            </a>
                                                            {!formDisabled && (
                                                                <button
                                                                    onClick={() =>
                                                                        handleMediaDelete(
                                                                            idx,
                                                                        )
                                                                    }
                                                                    className="text-red-600 text-sm font-semibold hover:text-red-800"
                                                                    disabled={
                                                                        formDisabled
                                                                    }
                                                                >
                                                                    Delete
                                                                </button>
                                                            )}
                                                        </div>

                                                        {/* Media Preview */}
                                                        <div className="mt-2">
                                                            {isImage && (
                                                                <a
                                                                    href={
                                                                        fileUrl
                                                                    }
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-block"
                                                                >
                                                                    <img
                                                                        src={
                                                                            fileUrl
                                                                        }
                                                                        alt={
                                                                            fileName
                                                                        }
                                                                        className="max-w-xs max-h-48 object-contain rounded border cursor-pointer hover:opacity-80 transition-opacity"
                                                                        onError={(
                                                                            e,
                                                                        ) => {
                                                                            e.target.style.display =
                                                                                "none";
                                                                        }}
                                                                    />
                                                                </a>
                                                            )}
                                                            {isAudio && (
                                                                <audio
                                                                    controls
                                                                    className="max-w-xs"
                                                                    onError={(
                                                                        e,
                                                                    ) => {
                                                                        e.target.style.display =
                                                                            "none";
                                                                    }}
                                                                >
                                                                    <source
                                                                        src={
                                                                            fileUrl
                                                                        }
                                                                    />
                                                                    Your browser
                                                                    does not
                                                                    support
                                                                    audio
                                                                    playback.
                                                                </audio>
                                                            )}
                                                            {isVideo && (
                                                                <video
                                                                    controls
                                                                    className="max-w-xs max-h-48"
                                                                    onError={(
                                                                        e,
                                                                    ) => {
                                                                        e.target.style.display =
                                                                            "none";
                                                                    }}
                                                                >
                                                                    <source
                                                                        src={
                                                                            fileUrl
                                                                        }
                                                                    />
                                                                    Your browser
                                                                    does not
                                                                    support
                                                                    video
                                                                    playback.
                                                                </video>
                                                            )}

                                                            {!isImage &&
                                                                !isAudio &&
                                                                !isVideo &&
                                                                !isDocument && (
                                                                    <a
                                                                        href={
                                                                            fileUrl
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-blue-600 hover:text-blue-800 text-sm underline"
                                                                    >
                                                                        Download{" "}
                                                                        {
                                                                            fileName
                                                                        }
                                                                    </a>
                                                                )}

                                                            {/* Error fallback message (hidden by default) */}
                                                            <div
                                                                style={{
                                                                    display:
                                                                        "none",
                                                                }}
                                                                className="text-red-600 text-sm"
                                                            >
                                                                Unable to load
                                                                media file.{" "}
                                                                <a
                                                                    href={
                                                                        fileUrl
                                                                    }
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="underline"
                                                                >
                                                                    Download
                                                                    instead
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    ) : (
                                        <p className="text-center text-gray-500 mb-6">
                                            No media files uploaded yet.
                                        </p>
                                    )}
                                    <div className="flex flex-col mt-6">
                                        <label className="text-navy font-semibold font-poppins">
                                            Video URL (YouTube or Vimeo only):
                                            *{" "}
                                        </label>
                                        <input
                                            name="videoUrl"
                                            value={formData.videoUrl}
                                            onChange={handleChange}
                                            placeholder="https://www.youtube.com/watch?v=..."
                                            className="p-2 border border-navy rounded-xl font-poppins w-full"
                                            disabled={formDisabled}
                                        />
                                        {formErrors.videoUrl && (
                                            <p className="text-red-600 text-sm mt-1">
                                                {formErrors.videoUrl}
                                            </p>
                                        )}
                                    </div>

                                    {/* Navigation Buttons */}
                                    <div className="flex justify-between mt-6">
                                        <button
                                            onClick={handleBack}
                                            className="py-2 px-12 bg-[#ffffff] border-2 border-navy text-navy rounded-xl shadow-md hover:bg-[#ffffff] font-bold font-poppins"
                                        >
                                            BACK
                                        </button>
                                        <button
                                            onClick={handleContinue}
                                            className="py-2 px-12 bg-navy text-white rounded-xl shadow-md hover:bg-navy-2 font-bold font-poppins"
                                        >
                                            REVIEW
                                        </button>
                                    </div>
                                </div>
                            )}
                            {activeSection === "review" && (
                                <div className="mt-4 sm:mt-6 space-y-8 sm:space-y-12">
                                    <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-navy mb-4 text-center font-poppins">
                                        REVIEW YOUR INFORMATION
                                    </h2>

                                    {/* Section Template */}
                                    {[
                                        {
                                            title: "General Info",
                                            key: "generalInfo",
                                            fields: [
                                                [
                                                    "First Name",
                                                    formData.firstName,
                                                ],
                                                [
                                                    "Last Name",
                                                    formData.lastName,
                                                ],
                                                ["Email", formData.email],
                                                ["Phone", formData.phone],
                                                ["Address", formData.address1],
                                                ["City", formData.city],
                                                ["State", formData.state],
                                                ["Zip Code", formData.zipCode],
                                            ],
                                        },
                                        {
                                            title: "Company Details",
                                            key: "companyDetails",
                                            fields: [
                                                [
                                                    "Company Name",
                                                    formData.companyName,
                                                ],
                                                ["DBA", formData.dba],
                                                [
                                                    "Company Website",
                                                    formData.companyWebsite,
                                                ],
                                                [
                                                    "Incorporated in US?",
                                                    formData.incorporatedInUS,
                                                ],
                                                [
                                                    "Executive Team in US?",
                                                    formData.executiveTeamUS,
                                                ],
                                                [
                                                    "Fundraising Goal",
                                                    formatCurrency(
                                                        formData.fundRaisingGoal,
                                                    ),
                                                ],
                                                [
                                                    "Previous Funds Raised",
                                                    formatCurrency(
                                                        formData.previousFundsRaised,
                                                    ),
                                                ],
                                                [
                                                    "Company Category",
                                                    formData.companyCategory,
                                                ],
                                                [
                                                    "Date Founded",
                                                    formData.dateFounded,
                                                ],
                                                [
                                                    "EIN",
                                                    formData.ein
                                                        ? `*****${String(
                                                              formData.ein,
                                                          ).slice(-4)}`
                                                        : formData.ein || "",
                                                    "ein",
                                                ],

                                                [
                                                    "Business Description",
                                                    formData.businessDescription,
                                                ],
                                                [
                                                    "Mission Statement",
                                                    formData.missionStatement,
                                                ],
                                            ],
                                        },
                                        {
                                            title: "Board Members",
                                            key: "companyDetails",
                                            customRender: (
                                                <ul className="ml-4 list-disc">
                                                    {(
                                                        formData.boardMembers ||
                                                        []
                                                    ).map((m, i) => (
                                                        <li key={i}>
                                                            {m.name} - {m.title}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ),
                                        },
                                        {
                                            title: "Uploads",
                                            key: "uploads",
                                            customRender: (
                                                <ul className="ml-4 list-disc">
                                                    {(() => {
                                                        const files = [];
                                                        // Handle individual files
                                                        if(
                                                            hasUploadedFile(
                                                                formData
                                                                    .uploadedFiles
                                                                    ?.businessPlan,
                                                            )
                                                        ) {
                                                            files.push(
                                                                `Business Plan: ${getOriginalFileName(
                                                                    formData
                                                                        .uploadedFiles
                                                                        .businessPlan,
                                                                )}`,
                                                            );
                                                        }
                                                        if(
                                                            hasUploadedFile(
                                                                formData
                                                                    .uploadedFiles
                                                                    ?.pitchDeck,
                                                            )
                                                        ) {
                                                            files.push(
                                                                `Pitch Deck: ${getOriginalFileName(
                                                                    formData
                                                                        .uploadedFiles
                                                                        .pitchDeck,
                                                                )}`,
                                                            );
                                                        }
                                                        if(
                                                            hasUploadedFile(
                                                                formData
                                                                    .uploadedFiles
                                                                    ?.financials,
                                                            )
                                                        ) {
                                                            files.push(
                                                                `Financials: ${getOriginalFileName(
                                                                    formData
                                                                        .uploadedFiles
                                                                        .financials,
                                                                )}`,
                                                            );
                                                        }
                                                        if(
                                                            hasUploadedFile(
                                                                formData
                                                                    .uploadedFiles
                                                                    ?.taxReturn,
                                                            )
                                                        ) {
                                                            files.push(
                                                                `Tax Return: ${getOriginalFileName(
                                                                    formData
                                                                        .uploadedFiles
                                                                        .taxReturn,
                                                                )}`,
                                                            );
                                                        }
                                                        // Handle others array
                                                        if(
                                                            Array.isArray(
                                                                formData
                                                                    .uploadedFiles
                                                                    ?.others,
                                                            )
                                                        ) {
                                                            formData.uploadedFiles.others.forEach(
                                                                (fileObj) => {
                                                                    if(
                                                                        fileObj &&
                                                                        typeof fileObj ===
                                                                            "object" &&
                                                                        fileObj.fileName
                                                                    ) {
                                                                        // Object with fileName property
                                                                        files.push(
                                                                            `Other: ${getOriginalFileName(
                                                                                fileObj.fileName,
                                                                            )}`,
                                                                        );
                                                                    } else if(
                                                                        typeof fileObj ===
                                                                        "string"
                                                                    ) {
                                                                        // String path - extract filename
                                                                        const fileName =
                                                                            fileObj
                                                                                .split(
                                                                                    "/",
                                                                                )
                                                                                .pop();
                                                                        files.push(
                                                                            `Other: ${getOriginalFileName(
                                                                                fileName,
                                                                            )}`,
                                                                        );
                                                                    } else if(
                                                                        fileObj &&
                                                                        typeof fileObj ===
                                                                            "object"
                                                                    ) {
                                                                        // Object without fileName - try to extract from other properties
                                                                        const fileName =
                                                                            fileObj.fullPath
                                                                                ? fileObj.fullPath
                                                                                      .split(
                                                                                          "/",
                                                                                      )
                                                                                      .pop()
                                                                                : fileObj.name ||
                                                                                  "Unknown file";
                                                                        files.push(
                                                                            `Other: ${getOriginalFileName(
                                                                                fileName,
                                                                            )}`,
                                                                        );
                                                                    }
                                                                },
                                                            );
                                                        }
                                                        return files.map(
                                                            (f, i) => (
                                                                <li key={i}>
                                                                    {f}
                                                                </li>
                                                            ),
                                                        );
                                                    })()}
                                                </ul>
                                            ),
                                        },

                                        {
                                            title: "Account Details",
                                            key: "accountDetails",
                                            fields: [
                                                [
                                                    "Contact Name",
                                                    formData.contactName,
                                                ],
                                                ["About Me", formData.aboutMe],
                                                [
                                                    "Title/Role",
                                                    formData.titleRole,
                                                ],
                                                ["LinkedIn", formData.linkedin],
                                                [
                                                    "Why Investment",
                                                    formData.whyInvestment,
                                                ],
                                                [
                                                    "Equity Stake",
                                                    formData.equityStake,
                                                ],
                                                [
                                                    "Achievements",
                                                    formData.achievements,
                                                ],
                                                [
                                                    "Experience",
                                                    formData.experience,
                                                ],
                                                ["Projects", formData.projects],
                                            ],
                                        },
                                        {
                                            title: "Investment Details",
                                            key: "investmentDetails",
                                            fields: [
                                                [
                                                    "Investment Name",
                                                    formData.investmentName,
                                                ],
                                                [
                                                    "Pitch",
                                                    formData.investmentPitch,
                                                ],
                                                [
                                                    "Investment Minimum",
                                                    formatCurrency(
                                                        formData.investmentMinimum,
                                                    ),
                                                ],
                                                [
                                                    "State",
                                                    formData.investmentState,
                                                ],
                                                [
                                                    "Funding History",
                                                    formatCurrency(
                                                        formData.fundingHistory,
                                                    ),
                                                ],
                                                [
                                                    "Revenue Growth",
                                                    formatCurrency(
                                                        formData.revenueGrowth,
                                                    ),
                                                ],
                                                [
                                                    "Equity Offered",
                                                    formData.equityOffered,
                                                ],
                                                [
                                                    "Revenue Status",
                                                    formData.revenueStatus,
                                                ],
                                                ["TAM", formData.tam],
                                                [
                                                    "Risks/Challenges",
                                                    formData.risksChallenges,
                                                ],
                                                [
                                                    "Competitive Advantage",
                                                    formData.competitiveAdvantage,
                                                ],
                                                [
                                                    "Exit Strategy",
                                                    formData.exitStrategy,
                                                ],
                                                [
                                                    "Valuation",
                                                    formatCurrency(
                                                        formData.valuation,
                                                    ),
                                                ],
                                                [
                                                    "Other Investors",
                                                    formData.otherInvestors,
                                                ],
                                                [
                                                    "Investor Perks",
                                                    normalizeInvestorPerks(
                                                        formData.investorPerks,
                                                    )
                                                        .filter(
                                                            (perk) =>
                                                                perk.amount ||
                                                                perk.description,
                                                        )
                                                        .map(
                                                            (perk) =>
                                                                `${formatInvestorPerkAmount(
                                                                    perk.amount,
                                                                )}${
                                                                    perk.amount &&
                                                                    perk.description
                                                                        ? " - "
                                                                        : ""
                                                                }${perk.description}`,
                                                        )
                                                        .join("; "),
                                                ],
                                            ],
                                        },
                                        {
                                            title: "Social Links",
                                            key: "socialLinks",
                                            customRender: (
                                                <ul className="ml-4 list-disc">
                                                    {(Array.isArray(
                                                        formData.socialLinks,
                                                    )
                                                        ? formData.socialLinks
                                                        : []
                                                    ).map((link, i) => (
                                                        <li key={i}>{link}</li>
                                                    ))}
                                                </ul>
                                            ),
                                        },
                                        {
                                            title: "Media & Press",
                                            key: "mediaAndPress",
                                            customRender: (
                                                <>
                                                    <ul className="ml-4 list-disc mb-2">
                                                        {(Array.isArray(
                                                            formData.mediaUploads,
                                                        )
                                                            ? formData.mediaUploads
                                                            : []
                                                        ).map((f, i) => (
                                                            <li key={i}>{f}</li>
                                                        ))}
                                                    </ul>
                                                    {formData.videoUrl && (
                                                        <p>
                                                            <strong className="text-navy">
                                                                Video URL:
                                                            </strong>{" "}
                                                            {formData.videoUrl}
                                                        </p>
                                                    )}
                                                </>
                                            ),
                                        },
                                    ].map((section, i) => (
                                        <div
                                            key={i}
                                            className="relative border-b pb-4 sm:pb-6"
                                        >
                                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-4 gap-2">
                                                <h3 className="text-base sm:text-lg font-bold text-navy">
                                                    {section.title}
                                                </h3>
                                                {!formDisabled && (
                                                    <button
                                                        onClick={() =>
                                                            setActiveSection(
                                                                section.key,
                                                            )
                                                        }
                                                        className="flex items-center space-x-1 text-xs sm:text-sm text-navy hover:underline self-start sm:self-auto"
                                                    >
                                                        <PencilIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                                                        <span>Edit</span>
                                                    </button>
                                                )}
                                            </div>

                                            {section.customRender ? (
                                                <div className="text-xs sm:text-sm text-gray-800">
                                                    {section.customRender}
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm text-gray-800">
                                                    {section.fields.map(
                                                        ([label, value]) => (
                                                            <div key={label}>
                                                                <span className="font-semibold text-navy block sm:inline">
                                                                    {label}:
                                                                </span>
                                                                <p className="break-words">
                                                                    {value ||
                                                                        "—"}
                                                                </p>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    <div className="flex flex-col sm:flex-row justify-between gap-4 mt-4 sm:mt-6">
                                        <button
                                            onClick={handleBack}
                                            className="w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-[#ffffff] border-2 border-navy text-navy rounded-xl shadow-md hover:bg-[#ffffff] font-bold font-poppins text-sm sm:text-base"
                                        >
                                            BACK
                                        </button>
                                        <button
                                            onClick={handleSubmit}
                                            disabled={formDisabled}
                                            className={`w-full sm:w-auto py-2 sm:py-2 px-8 sm:px-12 bg-navy text-white rounded-xl shadow-md hover:bg-navy-2 font-bold font-poppins text-sm sm:text-base ${
                                                formDisabled
                                                    ? "bg-navy text-white cursor-not-allowed"
                                                    : "bg-navy text-white hover:bg-navy"
                                            }`}
                                        >
                                            {formDisabled
                                                ? "SUBMITTED"
                                                : "SUBMIT"}
                                        </button>{" "}
                                    </div>
                                </div>
                            )}
                        </div>
                    </main>
                </div>
                {companyLogoCropImage && (
                    <div className="fixed inset-0 z-50 bg-black bg-opacity-70 flex items-center justify-center p-4">
                        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl h-[560px] flex flex-col p-4">
                            <div className="relative flex-1 bg-gray-900 rounded-lg overflow-hidden">
                                <Cropper
                                    image={companyLogoCropImage}
                                    crop={companyLogoCrop}
                                    zoom={companyLogoZoom}
                                    aspect={COMPANY_LOGO_CROP_ASPECT}
                                    minZoom={1}
                                    maxZoom={3}
                                    restrictPosition
                                    onCropChange={setCompanyLogoCrop}
                                    onZoomChange={setCompanyLogoZoom}
                                    onCropComplete={(
                                        _croppedArea,
                                        croppedPixels,
                                    ) => {
                                        setCompanyLogoCroppedAreaPixels(
                                            croppedPixels,
                                        );
                                    }}
                                />
                            </div>

                            <p className="text-gray-600 text-sm mt-3 font-poppins">
                                Crop your company logo for public campaign
                                cards. Keep the important content inside the
                                fixed portrait frame used across home,
                                investor, and dashboard pages.
                            </p>

                            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <label className="flex items-center gap-3 text-sm text-gray-700 font-poppins">
                                    Zoom
                                    <input
                                        type="range"
                                        min={1}
                                        max={3}
                                        step={0.05}
                                        value={companyLogoZoom}
                                        onChange={(e) =>
                                            setCompanyLogoZoom(
                                                Number(e.target.value),
                                            )
                                        }
                                        className="w-48 accent-navy"
                                    />
                                </label>

                                <div className="flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={handleCompanyLogoCropCancel}
                                        className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-poppins text-sm hover:bg-gray-300"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCompanyLogoCropSave}
                                        className="aqi-primary-action rounded-lg bg-navy px-4 py-2 text-sm text-white hover:bg-navy-2 font-poppins"
                                    >
                                        Save Crop
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {!isDashboardReview && <Footer />}
            </>
        </div>
    );
}

AQIEntrepreneurAccount.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
    }),
    setUser: PropTypes.func,
};

export default AQIEntrepreneurAccount;
